<template>
  <component
    :is="fullPage ? 'main' : 'el-dialog'"
    v-dialog-window="fullPage ? false : resizeDialogMap"
    v-bind="dialogProps"
    :class="fullPage ? 'waybill-map-route' : ''"
    v-on="dialogListeners"
  >
    <div v-loading="loading" class="waybill-map-viewer" :class="{ 'waybill-map-viewer--joint': jointMode, 'waybill-map-viewer--events': !shareMode, 'waybill-map-viewer--events-collapsed': !shareMode && eventPanelCollapsed, 'waybill-map-viewer--share': shareMode, 'waybill-map-viewer--share-info-collapsed': shareMode && h5InfoCollapsed }">
      <aside class="waybill-map-viewer__sidebar">
        <header v-if="!shareMode" class="viewer-event-header">
          <button type="button" class="viewer-event-header__title" :title="eventPanelCountHint" aria-label="查看全部事件" :aria-expanded="eventPopupType === 'all'" @click="openEventPopup('all')">事件信息 <span>{{ eventPanelCounts.total === null ? '—' : eventPanelCounts.total }}</span></button>
          <button type="button" class="viewer-event-header__toggle" :aria-expanded="!eventPanelCollapsed" :aria-label="eventPanelCollapsed ? '展开事件信息' : '收起事件信息'" :title="eventPanelCollapsed ? '展开事件信息' : '收起事件信息'" @click="toggleEventPanel"><i class="el-icon-arrow-right" /></button>
        </header>
        <div v-if="!shareMode && eventPanelCollapsed" class="viewer-event-compact" :title="eventPanelCountHint">
          <button type="button" aria-label="查看停车事件" :class="{ 'is-active': eventPopupType === 'parking' }" @click="openEventPopup('parking')">停车 <em>{{ eventPanelCounts.parking }}</em></button>
          <button type="button" aria-label="查看偏航事件" :class="{ 'is-active': eventPopupType === 'deviation' }" @click="openEventPopup('deviation')">偏航 <em>{{ eventPanelCounts.deviation === null ? '—' : eventPanelCounts.deviation }}</em></button>
          <button type="button" aria-label="查看离线事件" :class="{ 'is-active': eventPopupType === 'offline' }" @click="openEventPopup('offline')">离线 <em>{{ eventPanelCounts.offline === null ? '—' : eventPanelCounts.offline }}</em></button>
        </div>
        <div v-show="shareMode || !eventPanelCollapsed" class="viewer-event-panel">
        <nav v-if="!shareMode" class="viewer-event-controls" aria-label="事件视图">
          <button type="button" :class="{ 'is-active': eventPanelView === 'overview' }" :aria-pressed="eventPanelView === 'overview'" @click="setEventPanelView('overview')">全览</button>
          <button type="button" :class="{ 'is-active': eventPanelView === 'detail' }" :aria-pressed="eventPanelView === 'detail'" @click="setEventPanelView('detail')">明细</button>
          <el-popover placement="bottom-end" width="180" trigger="click">
            <div class="viewer-event-filter">
              <div>显示事件类型</div>
              <el-checkbox-group v-model="eventCategories" @change="setEventPanelView('detail')">
                <el-checkbox label="parking">停车事件</el-checkbox>
                <el-checkbox label="journey">行程节点</el-checkbox>
                <el-checkbox label="alarm">告警事件</el-checkbox>
              </el-checkbox-group>
              <div class="viewer-event-filter__parking">停车超过 <el-input-number v-model="parkingMinInput" :step="1" size="mini" controls-position="right" @change="changeParkingMinMinutes" /> 分钟</div>
            </div>
            <button slot="reference" type="button" class="viewer-event-controls__filter" :class="{ 'is-active': eventCategories.length !== 3 }" aria-label="筛选事件类型" title="筛选事件类型"><i class="el-icon-s-operation" /></button>
          </el-popover>
        </nav>
        <div v-show="shareMode || eventPanelView === 'overview'" class="viewer-event-overview">
        <section v-if="!shareMode && !jointMode && monitorNotice" class="viewer-monitor-notice" role="status">
          <strong>{{ monitorNotice.title }}</strong>
        </section>
        <section v-if="!shareMode && (jointMode || validPlaybackTracks.length >= 2)" class="viewer-track-summary">
          <template v-if="validPlaybackTracks.length >= 2">
          <dl>
            <dt>行驶总里程</dt><dd>{{ formatDistance(eventPanelSummary.distance) }}</dd>
            <dt>总耗时</dt><dd>{{ eventPanelSummary.seconds === null ? '-' : formatPanelDuration(eventPanelSummary.seconds) }}</dd>
            <dt>平均速度</dt><dd>{{ formatPanelSpeed(eventPanelSummary.averageSpeed) }}</dd>
            <dt>最高时速</dt><dd>{{ formatPanelSpeed(eventPanelSummary.maxSpeed) }}</dd>
          </dl>
          <div class="viewer-track-summary__parking"><span>停车 {{ parkingPoints.length }} 次</span><span>累计 {{ formatPanelDuration(parkingDurationTotal(parkingPoints)) }}</span></div>
          <p>统计范围：当前已加载轨迹{{ jointMode ? '，两单分别计算后汇总' : '' }}<template v-if="jointMode && jointPlayback.distance === null">；部分运单里程不可用，合计和均速暂不展示</template></p>
          </template>
          <p v-else>暂无{{ jointMode ? '可回放' : '本单' }}轨迹，暂不统计行驶里程和停车次数</p>
        </section>
        <el-tabs v-model="sidebarTab" stretch class="waybill-map-viewer__tabs">
          <el-tab-pane name="info">
            <span slot="label" class="viewer-tab-label">
              <i v-if="fullPage && !shareMode" class="el-icon-arrow-left viewer-tab-label__back" title="返回运单管理" @click.stop="$emit('close')" />
              <span>{{ jointMode ? '合并回放' : '运单信息' }}</span>
            </span>
        <template v-if="jointMode">
          <section class="viewer-section">
            <div class="viewer-section__title">两单汇总</div>
            <div class="viewer-basic-grid">
              <span>车辆</span><strong>{{ detail.vehicleNo || detail.vno || '-' }}</strong>
              <span>联合范围</span><strong>{{ formatDate(jointPlayback.range.startTime) }}<br>{{ formatDate(jointPlayback.range.endTime) }}</strong>
              <span>里程合计</span><strong>{{ formatDistance(jointPlayback.distance) }}</strong>
              <span>采样耗时</span><strong>{{ formatParkingDuration(jointPlayback.totalSeconds) }}</strong>
            </div>
            <div v-if="jointLoadError" class="viewer-empty">{{ jointLoadError }}</div>
            <div v-else-if="!tracks.length && !loading" class="viewer-empty">两条运单均无可回放轨迹</div>
          </section>
          <section v-for="order in jointPlayback.orders" :key="order.waybillNo" class="viewer-section">
            <div class="viewer-section__title">{{ order.waybillNo }}</div>
            <div class="viewer-basic-grid">
              <span>运输状态</span><strong>{{ statusText(order.waybill.transportStatus) }}</strong>
              <span>起点</span><strong>{{ order.waybill.startAreaName }} {{ order.waybill.startAddress || '-' }}</strong>
              <span>终点</span><strong>{{ order.waybill.endAreaName }} {{ order.waybill.endAddress || '-' }}</strong>
              <span>计划出发</span><strong>{{ formatDate(order.waybill.planDepartTime || order.waybill.planStartTime) }}</strong>
              <span>计划到达</span><strong>{{ formatDate(order.waybill.planArriveTime) }}</strong>
              <span>司机</span><strong>{{ order.waybill.driverName || '-' }} {{ order.waybill.driverPhone || '' }}</strong>
              <span>已行驶</span><strong>{{ formatDistance(order.distance) }}</strong>
              <span>行驶耗时</span><strong>{{ formatParkingDuration(order.drivingSeconds) }}</strong>
              <span>停车统计</span><strong>{{ order.parkingPoints.length }} 次 / {{ formatParkingDuration(parkingDurationTotal(order.parkingPoints)) }}</strong>
            </div>
          </section>
        </template>
        <template v-else>
        <section class="viewer-section">
          <div class="viewer-section__title">基础信息</div>
          <div class="viewer-basic-grid">
            <span>运单号</span><strong class="viewer-waybill-no">{{ detail.waybillNo || detail.transportNo || '-' }}<el-button v-if="shareMode" type="text" icon="el-icon-refresh" :loading="refreshingTrack" @click="refreshSharedWaybillTrack">刷新</el-button><el-button v-else-if="!embedMode && Number(detail.transportStatus) === 1" type="text" icon="el-icon-refresh" :loading="refreshingTrack" @click="refreshWaybillTrack">刷新</el-button></strong>
            <span>运输状态</span><strong>{{ beforePlannedDeparture && Number(detail.transportStatus) === 1 ? '监控已启动（待计划出发）' : statusText(detail.transportStatus) }}</strong>
            <span>车辆信息</span><strong>{{ detail.vehicleNo || detail.vno || '-' }}</strong>
            <span>司机</span><strong>{{ driverText }}</strong>
            <span>计划出发</span><strong>{{ formatDate(detail.planDepartTime || detail.planStartTime) }}</strong>
            <span>计划到达</span><strong>{{ formatDate(detail.planArriveTime) }}</strong>
            <span>规划路线</span><strong>{{ routeName || '未选择规划线路' }}</strong>
            <span>货物名称</span><strong>{{ detail.cargoName || detail.goodsName || '-' }}</strong>
          </div>
        </section>

        <section v-if="!shareMode && !embedMode && Number(detail.transportStatus) === 3" class="viewer-section">
          <div class="viewer-section__title">作废信息</div>
          <div class="viewer-basic-grid">
            <span>作废原因</span><strong>{{ detail.cancelReason || '未记录' }}</strong>
            <span>操作人</span><strong>{{ detail.canceledName || detail.canceledBy || '未记录' }}</strong>
            <span>作废时间</span><strong>{{ detail.canceledAt ? formatDate(detail.canceledAt) : '未记录' }}</strong>
          </div>
        </section>

        <section class="viewer-section">
          <div class="viewer-section__title">运输节点信息</div>
          <div class="viewer-node-list">
            <div v-for="node in nodes" :key="node.key" class="viewer-node-item" @click="focusNode(node)">
              <span class="viewer-node-item__badge" :class="`viewer-node-item__badge--${node.type}`">{{ node.type === 'stopover' ? node.label.replace(' ', '') : node.label }}</span>
              <div class="viewer-node-item__content">
                <div class="viewer-node-item__address">{{ node.fullAddress || '-' }}</div>
                <div class="viewer-node-item__meta">{{ nodeCoordinateText(node.lonLat) || '未设置坐标' }}<template v-if="node.planArriveTime"> · 预计 {{ formatDate(node.planArriveTime) }}</template></div>
              </div>
            </div>
          </div>
        </section>

        <section class="viewer-section">
          <div class="viewer-section__title">实时监控</div>
          <div class="viewer-monitor-grid">
            <div class="viewer-monitor-item"><span>当前状态</span><strong>{{ currentMonitorStatus }}</strong></div>
            <div class="viewer-monitor-item"><span>车辆状态</span><strong>{{ currentVehicleStatus }}</strong></div>
            <div class="viewer-monitor-item"><span>当前速度</span><strong>{{ currentTrack && currentTrack.speed !== null && currentTrack.speed !== undefined ? `${Number(currentTrack.speed).toFixed(1)} km/h` : '-' }}</strong></div>
            <div class="viewer-monitor-item"><span>已行驶</span><strong>{{ formatDistance(monitor && monitor.drivenDistance) }}</strong></div>
            <div class="viewer-monitor-item"><span>剩余里程</span><strong>{{ formatDistance(monitor && monitor.remainingDistance) }}<em v-if="remainingDistanceSourceText" class="viewer-source">{{ remainingDistanceSourceText }}</em></strong></div>
            <div class="viewer-monitor-item"><span>离线时长</span><strong>{{ ![2, 3, 4].includes(Number(detail.transportStatus)) && monitor && Number(monitor.offlineStatus) === 1 ? `${monitor.offlineTime || 0} 分钟` : '-' }}</strong></div>
            <div class="viewer-monitor-item viewer-monitor-item--wide"><span>最后定位</span><strong>{{ formatDate(currentTrack && currentTrack.locationTime) }}</strong></div>
            <div class="viewer-monitor-item viewer-monitor-item--wide"><span>预计到达</span><strong>{{ formatDate(monitor && monitor.estimatedArriveTime) }}<em v-if="estimatedArriveSourceText" class="viewer-source">{{ estimatedArriveSourceText }}</em></strong></div>
            <div class="viewer-monitor-item viewer-monitor-item--wide"><span>当前位置</span><strong>{{ currentLocationText }}<em v-if="currentAddressSourceText" class="viewer-source">{{ currentAddressSourceText }}</em></strong></div>
          </div>
        </section>

        <section class="viewer-section">
          <div class="viewer-section__title">途中围栏信息</div>
          <div v-if="mapFences.length" class="viewer-fence-list">
            <div v-for="fence in mapFences" :key="fence.fenceCode" class="viewer-fence-item" @click="focusFence(fence)">
              <strong>{{ fence.fenceName || fence.fenceCode }}</strong>
              <span>{{ fenceTypeText(fence.fenceType) }} · {{ eventTypeText(fence.eventType) }}</span>
            </div>
          </div>
          <div v-else class="viewer-empty">未关联途中围栏</div>
        </section>
        </template>
          </el-tab-pane>
          <el-tab-pane v-if="jointMode || (!shareMode && !embedMode)" name="events" label="行程明细">
            <section class="viewer-section">
              <div class="viewer-section__title">{{ jointMode ? '两单事件（按时间）' : '行程事件（按时间）' }}<el-button v-if="!jointMode" type="text" :disabled="journeyLoading" @click="loadJourneyEvents({ reset: true })">刷新</el-button></div>
              <div v-for="event in jointMode ? jointPlayback.events : journeyEvents" :key="`${event.waybillNo || ''}-${event.eventId || event.id}`" class="viewer-event-item" :class="{ 'viewer-event-item--locatable': journeyEventPoint(event) }" @click="focusJourneyEvent(event)">
                <strong v-if="jointMode">{{ event.waybillNo }}</strong>
                <span>{{ formatDate(event.eventTime) }}</span>
                <div>{{ event.nodeName || event.nodeType || '-' }}</div>
                <div>{{ event.address || '-' }}</div>
              </div>
              <div v-if="!jointMode && journeyLoading" class="viewer-empty">行程事件读取中…</div>
              <div v-if="!jointMode && journeyError" class="viewer-empty">{{ journeyError }}<el-button type="text" :disabled="journeyLoading" @click="loadJourneyEvents()">重试</el-button></div>
              <div v-else-if="!(jointMode ? jointPlayback.events.length : journeyEvents.length) && (jointMode || journeyLoaded)" class="viewer-empty">暂无行程事件</div>
              <template v-if="!jointMode && !journeyError">
                <el-button v-if="journeyCursor" type="text" :loading="journeyLoading" @click="loadJourneyEvents()">加载更多</el-button>
                <div v-else-if="journeyLoaded && journeyEvents.length" class="viewer-empty">已加载全部</div>
              </template>
            </section>
          </el-tab-pane>
          <el-tab-pane name="alarm">
            <span slot="label">告警信息 <em v-if="alarms.length" class="viewer-tab-count">{{ alarms.length }}</em></span>
            <section class="viewer-section viewer-section--alarm">
              <div class="viewer-section__title">最近告警</div>
              <div v-if="alarmLoadError" class="viewer-empty" role="alert">{{ alarmLoadError }}<el-button type="text" @click="loadAlarms">重试</el-button></div>
              <div v-if="alarms.length" class="viewer-alarm-list">
                <div v-for="alarm in alarms" :key="`${alarm.waybillNo || ''}-${alarm.alarmNo || alarm.id}`" class="viewer-alarm-item viewer-alarm-item--clickable" @click="focusAlarm(alarm)">
                  <el-tag :type="alarmTypeTag(alarm.alarmType)" size="mini">{{ alarmTypeText(alarm.alarmType) }}</el-tag>
                  <div class="viewer-alarm-item__content">
                    <strong v-if="jointMode">{{ alarm.waybillNo }}</strong>
                    <div>{{ alarm.alarmRemark || '-' }}</div>
                    <p v-if="alarmAddress(alarm)" class="viewer-alarm-item__address">{{ alarmAddress(alarm) }}</p>
                    <span>{{ formatDate(alarm.alarmTime || alarm.createTime) }}</span>
                  </div>
                </div>
              </div>
              <div v-else-if="!alarmLoadError" class="viewer-empty">暂无告警信息</div>
            </section>
          </el-tab-pane>
          <el-tab-pane name="parking">
            <span slot="label">停车点 <em v-if="parkingPoints.length" class="viewer-tab-count">{{ parkingPoints.length }}</em></span>
            <section class="viewer-section viewer-section--alarm">
              <div class="viewer-section__title">停车点</div>
              <div class="viewer-parking-filter">停车超过 <el-input-number v-model="parkingMinInput" :step="1" size="mini" controls-position="right" @change="changeParkingMinMinutes" /> 分钟</div>
              <div class="viewer-parking-summary">共 {{ parkingPoints.length }} 次 / {{ formatParkingDuration(parkingDurationTotal(parkingPoints)) }}</div>
              <div v-if="parkingPoints.length" class="viewer-alarm-list">
                <div v-for="parking in parkingPoints" :key="parking.key" class="viewer-alarm-item viewer-alarm-item--clickable viewer-parking-item" @click="focusParking(parking)">
                  <div class="viewer-parking-item__header">
                    <el-tag type="warning" size="mini">停车</el-tag>
                    <span v-if="jointMode">{{ parking.waybillNo }}</span>
                    <strong>{{ formatParkingDuration(parking.duration) }}</strong>
                  </div>
                  <div class="viewer-parking-item__address">{{ parking.locationName || '暂无定位地址' }}</div>
                  <div class="viewer-parking-item__time">
                    <span><em>开始</em>{{ formatDate(parking.startTime) }}</span>
                    <span><em>{{ parking.ended === false ? '末次观测' : '结束' }}</em>{{ formatDate(parking.endTime) }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="viewer-empty">暂无超过 {{ parkingMinMinutes }} 分钟的停车点</div>
            </section>
          </el-tab-pane>
        </el-tabs>
        </div>
        <div v-if="!shareMode && eventPanelView === 'detail'" class="viewer-event-detail">
          <div class="viewer-event-range">{{ eventPanelRangeText }}<template v-if="eventCategories.length !== 3"> · 筛选后 {{ filteredPanelEvents.length }} 条</template></div>
          <waybill-event-timeline :events="filteredPanelEvents" :show-waybill="jointMode" @locate="focusPanelEvent" />
          <div v-if="alarmLoadError" class="viewer-event-pagination" role="alert"><span>{{ alarmLoadError }}</span><el-button type="text" @click="loadAlarms">重试告警</el-button></div>
          <div v-if="!jointMode && !embedMode" class="viewer-event-pagination">
            <span v-if="journeyLoading">行程事件读取中…</span>
            <template v-else-if="journeyError"><span role="alert">{{ journeyError }}</span><el-button type="text" @click="loadJourneyEvents()">重试</el-button></template>
            <template v-else>
              <el-button v-if="journeyCursor" type="text" @click="loadJourneyEvents()">加载更多行程事件</el-button>
              <span v-else-if="journeyLoaded">行程事件已加载全部</span>
              <el-button type="text" @click="loadJourneyEvents({ reset: true })">刷新行程事件</el-button>
            </template>
          </div>
        </div>
        </div>
      </aside>
      <section v-if="!shareMode && eventPopupType" class="viewer-event-popup" :class="{ 'viewer-event-popup--compact': eventPanelCollapsed }" aria-label="事件列表" @keydown.esc.stop="eventPopupType = ''">
        <header><div><strong>{{ eventPopupTitle }}</strong><small>{{ jointMode ? '两单已加载数据' : (embedMode ? '当前授权数据' : '当前运单已加载数据') }}{{ eventPopupType === 'all' ? '；停车按段，解除过程见明细，不另计次数' : '' }}</small></div><button type="button" aria-label="关闭事件列表" @click="eventPopupType = ''"><i class="el-icon-close" /></button></header>
        <div v-if="eventPopupError" class="viewer-event-popup__error" role="alert">{{ eventPopupError }}<el-button v-if="!embedMode" type="text" @click="retryEventPopup">重试</el-button></div>
        <div class="viewer-event-popup__body">
          <button v-for="item in eventPopupItems" :key="item.key" type="button" class="viewer-event-popup__item" @click="focusPopupEvent(item)">
            <div><strong>{{ item.source.vehicleNo || detail.vehicleNo || detail.vno || '-' }}</strong><el-tag v-if="item.category === 'alarm'" size="mini" :type="handleStatusTag(item.source.handleStatus)">{{ handleStatusLabel(item.source.handleStatus) }}</el-tag></div>
            <small v-if="jointMode">{{ item.waybillNo }}</small>
            <template v-if="item.type === 'parking'">
              <p>停车 · {{ formatParkingDuration(item.source.duration) }}</p>
              <small>{{ formatDate(item.source.startTime) }} — {{ formatDate(item.source.endTime) }}{{ item.source.ended === false ? '（末次观测）' : '' }}</small>
              <p v-if="item.source.locationName">{{ item.source.locationName }}</p>
            </template>
            <template v-else-if="item.type === 'journey'">
              <p>{{ item.title }}<template v-if="item.remark && item.remark !== item.title"> · {{ item.remark }}</template></p>
              <small>{{ formatDate(item.time) }}</small>
              <p v-if="item.address">{{ item.address }}</p>
            </template>
            <template v-else>
              <p>{{ alarmTypeText(item.source.alarmType) }} · {{ item.source.alarmRemark || '-' }}</p>
              <small>{{ formatDate(item.source.alarmTime || item.source.createTime) }}</small>
            </template>
          </button>
          <div v-if="(eventPopupType === 'all' && journeyLoading) || (eventPopupType !== 'parking' && alarmLoading)" class="viewer-empty">事件读取中…</div>
          <div v-else-if="!eventPopupItems.length && !eventPopupError" class="viewer-empty">暂无此类事件</div>
        </div>
      </section>
      <div class="waybill-map-viewer__map-wrap">
        <trajectory-plug-in
          v-if="dialogVisible"
          ref="trajectory"
          class="waybill-map-viewer__map"
          :track-data="trajectoryTrackData"
          :park-mins-threshold="0"
          :use-default-data="false"
          :hide-search-endpoint-markers="true"
          :hide-track-end-marker="beforePlannedArrival"
          :vehicle-monitor-info="vehicleMonitorInfo"
          :suppress-auto-info-window="true"
          :force-solid-track-color="true"
          :hide-navigator-vehicle="!trajectoryPlaybackActive"
          :disable-point-preview-vehicle="true"
          :estimate-route-color="estimateRouteColor"
          :color-change="actualTrajectoryColorTriple"
          :display-primary-id="detail.vehicleNo || detail.vno || ''"
          @nav-progress="handleTrajectoryNavProgress"
          @navigator-vehicle-click="handleTrajectoryNavigatorVehicleClick"
        />
        <right-data-new-track
          v-if="shareAllowsFullTrip && playbackRunRoute.length > 1"
          ref="playback"
          class="waybill-map-viewer__playback"
          :is-collapsed="false"
          :is-expanded-num="playbackExpandedNum"
          :path-navigator-active="true"
          :run-route="playbackRunRoute"
          :parking-data="parkingTableData"
          :trip-summary="trajectoryTripSummary"
          :plate-from-api="detail.vehicleNo || detail.vno || ''"
          :display-override="trajectoryDisplayOverride"
          :trajectory-share-enabled="!draftMode && !shareMode && !embedMode && !jointMode"
          :trajectory-share-handler="createWaybillShareLink"
          :trajectory-share-revoke-handler="revokeWaybillShareLink"
          :initial-active-tab="shareMode || jointMode || initialView === 'track' ? 'second' : 'first'"
          @hook:mounted="initPlaybackPanel"
          @path-nav-control="handlePlaybackControl"
          @collapse="handlePlaybackCollapse"
          @playback-range-change="handlePlaybackRangeChange"
          @locate-on-map="handleLocateOnMap"
          @update-display-override="updateTrajectoryDisplayOverride"
          @prepare-capture-layout="prepareCaptureLayout"
        />
        <div v-if="shareMode && shareAllowsFullTrip && playbackRunRoute.length > 1" class="waybill-map-viewer__track-detail-controls">
          <button v-if="playbackExpandedNum === 3" type="button" aria-label="显示轨迹明细" @click.stop="setH5TrackDetails(1)"><i class="el-icon-tickets" />轨迹明细</button>
          <template v-else>
            <button v-if="playbackExpandedNum === 1" type="button" aria-label="全屏轨迹明细" @click.stop="setH5TrackDetails(2)"><i class="el-icon-arrow-up" />全屏明细</button>
            <button v-else type="button" aria-label="下拉轨迹明细" @click.stop="setH5TrackDetails(1)"><i class="el-icon-arrow-down" />下拉半屏</button>
            <button type="button" aria-label="收起轨迹明细" @click.stop="setH5TrackDetails(3)"><i class="el-icon-close" />收起</button>
          </template>
        </div>
        <transport-service-map-toolbar
          class="waybill-map-viewer__toolbar"
          :tools-menu-open="toolsMenuOpen"
          :show-satellite="showSatellite"
          :hide-parking-markers="hideParkingMarkers"
          :hide-endpoint-markers="hideEndpointMarkers"
          :report-points-only="reportPointsOnly"
          hide-color-config
          ranging-only
          @action="onToolbarAction"
        />
        <div class="waybill-map-viewer__layers">
          <div class="waybill-map-viewer__layers-title">地图图层</div>
          <el-checkbox v-if="!jointMode" v-model="planRouteVisible" @change="renderMap"><i class="layer-line layer-line--plan" />规划路线</el-checkbox>
          <el-checkbox v-if="shareAllowsFullTrip" v-model="actualTrajectoryVisible" :disabled="!trajectoryTrackData.traceList.length" @change="syncTrajectoryToolStates()"><i class="layer-line layer-line--actual" :style="{ background: actualTrajectoryColor }" />实际轨迹</el-checkbox>
          <el-checkbox v-if="estimateRoute.length > 1" v-model="estimateRouteVisible" @change="syncEstimateRoute"><i class="layer-line layer-line--estimate" />预计轨迹</el-checkbox>
        </div>
        <button v-if="shareMode" type="button" class="waybill-map-viewer__info-collapse-toggle" :aria-label="h5InfoCollapsed ? '展开运单信息' : '收起运单信息'" @click.stop="toggleH5InfoPanel"><i :class="h5InfoCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'" />{{ h5InfoCollapsed ? '展开信息' : '收起信息' }}</button>
      </div>
    </div>
    <span v-if="!fullPage" slot="footer"><el-button type="primary" @click="dialogVisible = false">关闭</el-button></span>
  </component>
</template>

<script>
import dayjs from 'dayjs'
import { createWaybillShare, getAlarmList, getJourneyNodes, getMonitorTrack, getRouteDetail, getWaybillDetail, isRequestSuccess, refreshMonitorTrack, revokeWaybillShare } from './lingdongApi'
import { buildJointPlayback } from './jointPlayback'
import TrajectoryPlugIn from '@/views/track/trajectoryPlugIn.vue'
import RightDataNewTrack from '@/components/RightDataNewTrack.vue'
import WaybillEventTimeline from './WaybillEventTimeline.vue'
import { buildWaybillEvents, summarizeWaybillTracks } from './waybillEvents'
import { handleStatusLabel, handleStatusTag } from './alarmStatus'
import TransportServiceMapToolbar from '@/views/transportService/TransportServiceMapToolbar.vue'
import { convertRunRouteToTrackData } from '@/views/transportService/trajectoryData-track'
import { loadAmapSDK } from '@/utils/amapConfig'
import loadIcon from '@/assets/icon_guiji_zhuang.svg'
import unloadIcon from '@/assets/icon_guiji_xie.svg'
import carIcon from '@/assets/lv.png'
import { getTrajectoryColorSettings } from '@/utils/trajectoryColorSettings'
import { isFullPlaybackClip } from '@/utils/playbackRangeClip'
import { formatLonLat, wgs84LonLatToGcj02Point, wgs84RouteToGcj02 } from './coordinate'
import { tiandituReverseGeocode } from '@/utils/tiandituGeocoder'
import { isValidResolvedAddress, resolveAddressByCoord as resolveAddressByCoordUtil } from '@/utils/coordAddressResolver'

/* global AMap */
const NODE_STYLE = {
  stopover: { color: '#e6a23c' }
}

const ALARM_TYPE_TEXT = {
  1: '晚发', 2: '晚到', 3: '线路偏离', 4: '超时停车', 5: '在途离线', 6: '进入起始地围栏', 7: '驶出起始地围栏', 8: '进入目的地围栏', 9: '驶出目的地围栏', B: '进入经停点', C: '驶出经停点', D: '进入途中围栏', E: '驶出途中围栏', F: '途中围栏区域内停车', G: '途中围栏驻留超时', H: '超速驾驶', I: '疲劳驾驶', J: '疲劳驾驶', 10: '灾害天气'
}

export default {
  name: 'WaybillMapDialog',
  components: { TrajectoryPlugIn, RightDataNewTrack, TransportServiceMapToolbar, WaybillEventTimeline },
  props: {
    visible: { type: Boolean, default: false },
    fullPage: { type: Boolean, default: false },
    draftMode: { type: Boolean, default: false },
    waybill: { type: Object, default: () => ({}) },
    jointDetails: { type: Array, default: () => [] },
    initialView: { type: String, default: '' },
    stopovers: { type: Array, default: () => [] },
    fences: { type: Array, default: () => [] },
    shareMode: { type: Boolean, default: false },
    shareScope: { type: String, default: '' },
    embedMode: { type: Boolean, default: false },
    initialMonitorData: { type: Object, default: null },
    initialAlarms: { type: Array, default: () => [] },
    initialFences: { type: Array, default: () => [] },
    initialRoutePlan: { type: Object, default: null },
    shareRefreshHandler: { type: Function, default: null }
  },
  data() {
    return {
      dialogVisible: false,
      fullscreen: false,
      loading: false,
      mapOpened: false,
      mapOpenSequence: 0,
      map: null,
      routePlan: null,
      mapDataSequence: 0,
      mapFences: [],
      routePoints: [],
      infoWindow: null,
      externalOverlays: [],
      monitorData: null,
      monitorNow: Date.now(),
      monitorLoadError: '',
      jointEntries: [],
      jointNow: Date.now(),
      jointLoadSequence: 0,
      jointLoadError: '',
      trackCursor: null,
      backfillCursor: null,
      trackHasMore: false,
      backfillHasMore: false,
      backfillBaselinePending: false,
      alarms: [],
      alarmLoadError: '',
      alarmLoadSequence: 0,
      alarmLoading: false,
      parkingAddressMap: {},
      parkingMinMinutes: 10,
      parkingMinInput: 10,
      alarmAddressMap: {},
      refreshTimer: null,
      sidebarTab: 'info',
      eventPanelView: 'overview',
      eventPanelCollapsed: true,
      eventPopupType: '',
      eventCategories: ['parking', 'journey', 'alarm'],
      journeyEvents: [],
      journeyCursor: null,
      journeyLoading: false,
      journeyError: '',
      journeyLoadSequence: 0,
      journeyLoaded: false,
      planRouteVisible: true,
      actualTrajectoryVisible: true,
      estimateRouteVisible: false,
      estimateRouteColor: '#7c4dff',
      toolsMenuOpen: false,
      showSatellite: false,
      hideParkingMarkers: false,
      hideEndpointMarkers: true,
      reportPointsOnly: false,
      refreshingTrack: false,
      trajectoryPlaybackActive: false,
      playbackRangeClipped: false,
      playbackTrackSnapshot: [],
      playbackExpandedNum: 3,
      h5InfoCollapsed: false,
      trajectoryDisplayOverride: null,
      vehicleInfoFollowing: false,
      lastVehicleInfoSyncAt: 0,
      actualTrajectoryColorValue: String(getTrajectoryColorSettings().solidColor || '').split(',')[0] || '#2FAE55'
    }
  },
  computed: {
    dialogProps() {
      if (this.fullPage) return {}
      return {
        title: '运单地图',
        visible: this.dialogVisible,
        width: '1200px',
        top: '4vh',
        fullscreen: this.fullscreen,
        modal: true,
        showClose: true,
        customClass: 'waybill-map-dialog',
        appendToBody: true,
        closeOnClickModal: false
      }
    },
    dialogListeners() {
      if (this.fullPage) return {}
      return {
        'update:visible': value => { this.dialogVisible = value },
        opened: this.openMap,
        closed: this.closeMap
      }
    },
    detail() { return this.waybill || {} },
    journeyWaybillNo() { return this.detail.waybillNo || this.detail.transportNo || '' },
    jointMode() { return !this.shareMode && !this.embedMode && this.jointDetails.length === 2 },
    jointPlayback() { return buildJointPlayback(this.jointEntries, this.jointNow, this.parkingMinMinutes) },
    shareAllowsFullTrip() { return !this.shareMode || this.shareScope === 'FULL_TRIP' },
    driverText() {
      const name = this.detail.driverName || this.detail.driverId || ''
      if (this.shareMode) return name || '-'
      const phone = this.detail.driverPhone || ''
      return name ? `${name}${phone ? `（${phone}）` : ''}` : (phone || '-')
    },
    monitor() { return this.monitorData && this.monitorData.monitor ? this.monitorData.monitor : null },
    vehicleMonitorInfo() {
      if (this.jointMode) return null
      return {
        remainingDistance: this.formatDistance(this.monitor && this.monitor.remainingDistance),
        estimatedArriveTime: this.formatDate(this.monitor && this.monitor.estimatedArriveTime)
      }
    },
    tracks() { return this.jointMode ? this.jointPlayback.tracks : (this.monitorData && Array.isArray(this.monitorData.tracks) ? this.monitorData.tracks : []) },
    playbackSourceTracks() {
      return (this.trajectoryPlaybackActive || this.playbackRangeClipped) && this.playbackTrackSnapshot.length
        ? this.playbackTrackSnapshot
        : this.tracks
    },
    validPlaybackTracks() {
      return this.playbackSourceTracks.filter(item => this.hasTrackCoordinate(item) && this.trackTimestamp(item) > 0)
    },
    actualTrajectoryColor() { return this.actualTrajectoryColorValue },
    actualTrajectoryColorTriple() {
      return `${this.actualTrajectoryColor},${this.actualTrajectoryColor},${this.actualTrajectoryColor}`
    },
    eventPanelEvents() {
      return buildWaybillEvents({ parkingPoints: this.parkingPoints, journeyEvents: this.jointMode ? this.jointPlayback.events : this.journeyEvents, alarms: this.alarms.map(alarm => ({ ...alarm, alarmAddress: this.alarmAddress(alarm) })), waybillNo: this.journeyWaybillNo, alarmLabel: alarm => this.alarmTypeText(alarm.alarmType) })
    },
    eventOccurrences() {
      const scope = item => String(item.waybillNo || this.journeyWaybillNo)
      const alarms = new Map()
      this.alarms.forEach((alarm, index) => {
        const key = `${scope(alarm)}:${alarm.alarmNo || alarm.id || index}`
        if (Number(alarm.alarmFlag) !== 2 && !alarms.has(key)) alarms.set(key, alarm)
      })
      // 全部列表复用明细去重结果；停车按段，解除保留在明细中，不再算一次发生。
      return this.eventPanelEvents.filter(event => !event.resolved && !(event.category === 'parking' && event.title === '停车结束')).map(event => {
        const alarmNo = event.category === 'alarm' ? event.source.alarmNo || event.source.id : event.source.sourceAlarmNo
        const alarm = alarms.get(`${event.waybillNo}:${alarmNo}`) || (event.category === 'alarm' ? event.source : null)
        const alarmType = String(alarm && alarm.alarmType)
        let type = event.category
        if (alarmType === '3') type = 'deviation'
        else if (alarmType === '5') type = 'offline'
        return { ...event, type, source: alarm && (event.category === 'alarm' || type === 'deviation' || type === 'offline') ? alarm : event.source }
      })
    },
    eventPanelCounts() {
      const counts = { parking: 0, deviation: 0, offline: 0, total: this.eventOccurrences.length }
      this.eventOccurrences.forEach(event => { if (Object.prototype.hasOwnProperty.call(counts, event.type)) counts[event.type] += 1 })
      if (this.alarmLoadError || this.alarmLoading || this.jointLoadError) return { ...counts, deviation: null, offline: null, total: null }
      if (this.journeyError || this.journeyLoading) counts.total = null
      return counts
    },
    eventPanelCountHint() {
      const scope = this.jointMode ? '两单已加载' : (this.embedMode ? '当前授权数据中的' : '已加载')
      return `${scope}停车、行程节点和全部类型告警。停车按段；关联节点与告警去重，解除过程见明细，不另计次数。${this.alarmLoading || this.journeyLoading ? '事件读取中。' : ''}${this.alarmLoadError || this.journeyError || this.jointLoadError ? '部分事件读取失败，总数暂不可用。' : ''}`
    },
    eventPopupTitle() { return { all: '全部事件', parking: '停车事件', deviation: '偏航事件', offline: '离线事件' }[this.eventPopupType] || '事件列表' },
    eventPopupError() { return this.jointLoadError || (this.eventPopupType === 'parking' ? '' : [this.alarmLoadError, this.eventPopupType === 'all' ? this.journeyError : ''].filter(Boolean).join('；')) },
    eventPopupItems() {
      return this.eventOccurrences.filter(item => this.eventPopupType === 'all' || item.type === this.eventPopupType).sort((left, right) => right.time - left.time)
    },
    filteredPanelEvents() {
      return this.eventPanelEvents.filter(event => this.eventCategories.includes(event.category) || (event.source.sourceAlarmNo && this.eventCategories.includes('alarm')))
    },
    eventPanelSummary() {
      const summary = summarizeWaybillTracks(this.validPlaybackTracks, this.jointMode ? this.jointPlayback.distance : this.monitor && this.monitor.drivenDistance, this.jointMode ? this.jointPlayback.totalSeconds : undefined)
      if (this.jointMode && this.jointPlayback.distance === null) return { ...summary, distance: null, averageSpeed: null }
      return summary
    },
    eventPanelRangeText() {
      if (this.jointMode) return '两单已加载的行程、停车和告警事件'
      if (this.embedMode) return '当前授权数据中的停车和告警事件'
      return '当前运单已加载的行程、停车和全部类型告警；解除过程保留在明细中'
    },
    parkingPoints() {
      if (this.jointMode) return this.jointPlayback.parkingPoints.map(item => {
        const order = this.jointPlayback.orders.find(order => order.waybillNo === item.waybillNo)
        const orderTracks = order && order.monitorData && order.monitorData.tracks || []
        const latest = orderTracks.reduce((selected, track) => !selected || this.trackTimestamp(track) > this.trackTimestamp(selected) ? track : selected, null)
        return { ...item, ended: !(latest && this.trackTimestamp(latest) === Number(item.endTime) && (Number(latest.speed) || 0) <= 5), locationName: item.locationName || this.parkingAddressMap[this.parkingAddressKey(item)] || '' }
      })
      const list = this.tracks.filter(item => this.hasTrackCoordinate(item) && this.trackTimestamp(item)).slice()
        .sort((a, b) => this.trackTimestamp(a) - this.trackTimestamp(b))
      if (list.length < 2) return []
      const result = []
      let parkingStart = null
      const finishParking = (previous, ended, endTime = this.trackTimestamp(previous)) => {
        if (!parkingStart) return
        const startTime = this.trackTimestamp(parkingStart)
        const duration = Math.max(0, endTime - startTime) / 1000
        if (duration > this.parkingMinMinutes * 60) {
          result.push({
            key: `${previous.id || ''}-${startTime}`,
            longitude: Number(previous.longitude),
            latitude: Number(previous.latitude),
            locationName: previous.locationName || this.parkingAddressMap[this.parkingAddressKey(previous)] || '',
            startTime,
            endTime,
            duration,
            ended,
            vehicleNo: previous.vehicleNo || previous.vno || this.detail.vehicleNo || this.detail.vno || ''
          })
        }
        parkingStart = null
      }
      for (let index = 0; index < list.length; index++) {
        const current = list[index]
        const speed = current.speed === null || current.speed === undefined || String(current.speed).trim() === '' || !Number.isFinite(Number(current.speed)) || Number(current.speed) < 0 ? null : Number(current.speed)
        if (speed !== null && speed <= 5) {
          if (!parkingStart) parkingStart = current
        } else if (parkingStart) {
          // 缺少速度只能记为截至上个有效点的观测区间，不能认定车辆已驶离。
          finishParking(list[index - 1], speed !== null, this.trackTimestamp(speed === null ? list[index - 1] : current))
        }
      }
      finishParking(list[list.length - 1], false)
      return result
    },
    parkingParkArray() {
      return this.parkingPoints.map(item => ({
        waybillNo: item.waybillNo,
        parkBte: item.startTime,
        parkEte: item.endTime,
        parkLon: item.longitude * 600000,
        parkLat: item.latitude * 600000,
        parkAdr: item.locationName,
        vclN: item.vehicleNo
      }))
    },
    parkingTableData() {
      return this.parkingPoints.map(item => ({
        waybillNo: item.waybillNo,
        duration: this.formatParkingDuration(item.duration),
        startTime: this.formatDate(item.startTime),
        endTime: this.formatDate(item.endTime),
        location: item.locationName || '',
        lon: Number(item.longitude) * 600000,
        lat: Number(item.latitude) * 600000
      }))
    },
    trajectoryTripSummary() {
      const sourceTracks = this.playbackSourceTracks
      const first = this.trackTimestamp(sourceTracks[0])
      const last = this.trackTimestamp(sourceTracks[sourceTracks.length - 1])
      const duration = this.jointMode ? this.jointPlayback.totalSeconds : Math.max(0, Math.floor((last - first) / 1000))
      const hours = Math.floor(duration / 3600)
      const minutes = Math.floor((duration % 3600) / 60)
      const seconds = duration % 60
      return {
        distance: this.formatDistance(this.jointMode ? this.jointPlayback.distance : this.monitor && this.monitor.drivenDistance).replace(' ', ''),
        duration: `${hours}时${minutes}分${seconds}秒`
      }
    },
    trajectoryTrackData() {
      const runRoute = this.playbackSourceTracks.map((item) => ({
        lon: Number(item.longitude) * 600000,
        lat: Number(item.latitude) * 600000,
        gtm: this.toGtm(item.locationTime),
        spd: item.speed,
        mil: item.mileage,
        hgt: item.altitude,
        agl: item.direction,
        vclN: item.vehicleNo
      })).filter(item => Number.isFinite(item.lon) && Number.isFinite(item.lat) && item.gtm)
      const data = convertRunRouteToTrackData(runRoute, {
        totalMileage: this.jointMode ? this.jointPlayback.distance : this.monitor && this.monitor.drivenDistance,
        avgSpeed: this.monitor && this.monitor.currentSpeed,
        parkArray: this.parkingParkArray
      })
      if (this.jointMode) {
        data.segmentKeys = this.playbackSourceTracks.filter(item => Number.isFinite(Number(item.longitude)) && Number.isFinite(Number(item.latitude)) && this.toGtm(item.locationTime)).map(item => item.segmentKey)
        data.traceOverviewInfo.totalTimes = this.jointPlayback.totalSeconds * 1000
      }
      return data
    },
    playbackRunRoute() {
      return this.playbackSourceTracks.map((item) => ({
        lon: Number(item.longitude) * 600000,
        lat: Number(item.latitude) * 600000,
        gtm: this.toGtm(item.locationTime),
        spd: item.speed,
        mil: item.mileage,
        hgt: item.altitude,
        agl: item.direction,
        waybillNo: this.jointMode ? (item.waybillNos ? item.waybillNos.join('、') : item.waybillNo) : undefined,
        segmentKey: item.segmentKey,
        vclN: item.vehicleNo || this.detail.vehicleNo || this.detail.vno
      })).filter(item => Number.isFinite(item.lon) && Number.isFinite(item.lat) && item.gtm)
    },
    estimateRoute() {
      if (this.jointMode || !this.detail.routeId) return []
      const value = this.monitor && this.monitor.estimateRouteJson
      if (!value) return []
      try {
        const points = Array.isArray(value) ? value : JSON.parse(value)
        return (Array.isArray(points) ? points : []).map(item => ({
          lon: Number(item.longitude != null ? item.longitude : item.lon) * 600000,
          lat: Number(item.latitude != null ? item.latitude : item.lat) * 600000
        })).filter(item => Number.isFinite(item.lon) && Number.isFinite(item.lat))
      } catch (e) {
        return []
      }
    },
    currentTrack() {
      const candidates = [...this.tracks]
      if (this.monitorData && this.monitorData.currentTrack) candidates.push(this.monitorData.currentTrack)
      if (!this.jointMode && this.monitor) candidates.unshift({
        longitude: this.monitor.currentLongitude,
        latitude: this.monitor.currentLatitude,
        locationTime: this.monitor.locationTime,
        speed: this.monitor.currentSpeed,
        direction: this.monitor.currentDirection,
        mileage: this.monitor.currentMileage,
        locationName: this.monitor.currentAddress,
        vehicleNo: this.detail.vehicleNo || this.detail.vno
      })
      return candidates.filter(item => this.hasTrackCoordinate(item) && this.trackTimestamp(item) > 0).reduce((latest, item) => {
        if (!latest || this.trackTimestamp(item) >= this.trackTimestamp(latest)) return item
        return latest
      }, null)
    },
    beforePlannedDeparture() {
      const departure = this.trackTimestamp({ locationTime: this.detail.planDepartTime || this.detail.planStartTime })
      return !this.jointMode && [0, 1].includes(Number(this.detail.transportStatus)) && !this.detail.actualDepartTime && departure > this.monitorNow
    },
    beforePlannedArrival() {
      const arrival = this.trackTimestamp({ locationTime: this.detail.planArriveTime })
      return !this.jointMode && arrival > this.monitorNow
    },
    monitorNotice() {
      const status = Number(this.detail.transportStatus)
      const queried = Boolean(this.monitor && this.monitor.lastSyncTime)
      const failed = this.monitorLoadError || (this.monitor && this.monitor.syncStatus === 'FAILED')
      let title
      if (this.beforePlannedDeparture) {
        title = '未到计划出发时间'
      } else if (status === 0) {
        title = '尚未启动监控'
      } else if (failed) {
        title = '位置查询失败，请稍后重试'
      } else if (this.validPlaybackTracks.length < 2) {
        if (this.currentTrack) title = '已获取位置，暂无本单轨迹'
        else if (queried) title = '暂无车辆定位和本单轨迹'
        else title = status === 1 ? '等待首次查询结果' : '暂无本单轨迹'
      } else return null
      return { title }
    },
    currentMonitorStatus() {
      const status = Number(this.detail.transportStatus)
      if ([2, 3, 4].includes(status)) return { 2: '已完成', 3: '已作废', 4: '监控超期' }[status]
      if (status === 0) return '未启动'
      if (this.beforePlannedDeparture && !this.currentTrack) return '待计划出发'
      if (this.currentTrack) return '已定位'
      if (this.monitorLoadError || (this.monitor && this.monitor.syncStatus === 'FAILED')) return '查询失败'
      return this.monitor && this.monitor.lastSyncTime ? '暂无定位' : '等待定位'
    },
    currentVehicleStatus() {
      if ([2, 3, 4].includes(Number(this.detail.transportStatus))) return '已停止监控'
      if (!this.monitor) return '未知'
      if (!this.currentTrack && !this.monitor.lastSuccessTime) return '未知'
      if (this.monitor.monitorStatus === 'OFFLINE' || (this.monitor.lastSuccessTime && Number(this.monitor.offlineStatus) === 1)) return '离线'
      return this.currentTrack && (this.monitor.monitorStatus === 'ONLINE' || this.monitor.lastSuccessTime) ? '在线' : '未知'
    },
    currentLocationText() {
      return (this.currentTrack && this.currentTrack.locationName) || (this.monitor && this.monitor.currentAddress) || '-'
    },
    currentAddressSourceText() {
      return this.monitor && this.monitor.currentAddressSource === 'REVERSE_GEOCODE' ? '（坐标解析）' : ''
    },
    remainingDistanceSourceText() {
      const source = this.monitor && this.monitor.remainingDistanceSource
      return source === 'ROUTE_ESTIMATE' ? '（路线估算）' : (source === 'DIRECT_ESTIMATE' ? '（直线估算）' : '')
    },
    estimatedArriveSourceText() {
      return this.monitor && this.monitor.estimatedArriveSource === 'ESTIMATE' ? '（系统估算）' : ''
    },
    routeName() { return this.routePlan && this.routePlan.routeName },
    nodes() {
      if (this.jointMode) return this.jointEntries.flatMap(entry => {
        const bill = entry.waybill || {}
        const no = bill.waybillNo || bill.transportNo
        return [
          this.normalizeNode({ areaName: bill.startAreaName, address: bill.startAddress, lonLat: bill.startLonlat }, 'origin', `${no} 起点`, `${no}-start`),
          this.normalizeNode({ areaName: bill.endAreaName, address: bill.endAddress, lonLat: bill.endLonlat }, 'target', `${no} 终点`, `${no}-end`)
        ]
      })
      const start = this.normalizeNode(this.detail.start || {
        areaCodes: this.detail.startAreaCodes,
        areaName: this.detail.startAreaName,
        address: this.detail.startAddress,
        lonLat: this.detail.startLonlat,
        contactName: this.detail.startContactName,
        contactPhone: this.detail.startContactPhone,
        radius: this.detail.startFenceRadius
      }, 'origin', '起点', 'start')
      const stopovers = (this.stopovers || []).map((item, index) => this.normalizeNode(item.stopoverAddress === undefined ? item : {
        areaCodes: item.stopoverAreaCodes,
        areaName: item.stopoverAreaName,
        address: item.stopoverAddress,
        lonLat: item.stopoverLonlat,
        contactName: item.stopoverContactName,
        contactPhone: item.stopoverContactPhone,
        radius: item.stopoverRadius,
        planArriveTime: item.planArriveTime
      }, 'stopover', `经停点 ${index + 1}`, `stopover-${index}`))
      const target = this.normalizeNode(this.detail.target || {
        areaCodes: this.detail.endAreaCodes,
        areaName: this.detail.endAreaName,
        address: this.detail.endAddress,
        lonLat: this.detail.endLonlat,
        contactName: this.detail.endContactName,
        contactPhone: this.detail.endContactPhone,
        radius: this.detail.endFenceRadius
      }, 'target', '目的地', 'target')
      return [start, ...stopovers, target]
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) { this.dialogVisible = value }
    },
    dialogVisible(value) { this.$emit('update:visible', value) },
    sidebarTab(value) {
      if (value === 'parking') this.resolveParkingAddresses()
      if (value === 'alarm') this.resolveAlarmAddresses()
      if (value === 'events' && (!this.journeyLoaded || Number(this.detail.transportStatus) === 1)) this.loadJourneyEvents({ reset: true, all: true })
    },
    journeyWaybillNo() {
      this.resetJourneyEvents()
      this.alarmLoadSequence++
      this.alarmLoading = false
      this.alarms = []
      this.alarmLoadError = ''
      if (this.mapOpened && !this.shareMode && !this.jointMode) {
        this.loadAlarms()
        this.loadJourneyEvents({ reset: true, all: true })
      }
    },
    initialMonitorData(value) {
      if (!this.mapOpened || !value) return
      if (this.shareMode) {
        this.setInitialShareMonitorData(value)
        this.$nextTick(() => this.renderTrajectoryOverlays().catch(() => {}))
        return
      }
      if (this.monitorData && this.monitorData.monitor) return
      this.monitorData = { monitor: value, tracks: [], currentTrack: null }
    },
    initialAlarms(value) {
      if (!this.shareMode || !this.mapOpened) return
      this.alarms = Array.isArray(value) ? value : []
      if (this.sidebarTab === 'alarm') this.resolveAlarmAddresses()
    },
    initialFences(value) {
      if (!this.shareMode || !this.mapOpened) return
      this.mapFences = Array.isArray(value) ? value : []
      this.$nextTick(() => this.renderMap())
    },
    initialRoutePlan(value) {
      if (!this.shareMode || !this.mapOpened) return
      this.mapDataSequence++
      this.routePlan = this.detail.routeId ? (value || null) : null
      this.loadRoutePoints().then(() => this.renderMap())
    },
    'detail.routeId'(value) {
      this.mapDataSequence++
      if (!value) {
        this.routePlan = null
        this.routePoints = []
        this.estimateRouteVisible = false
        this.syncEstimateRoute()
        if (this.mapOpened) this.renderMap(false)
      }
    }
  },
  mounted() {
    if (this.fullPage && this.dialogVisible) this.$nextTick(() => this.openMap())
  },
  beforeDestroy() {
    this.mapOpenSequence++
    this.mapOpened = false
    this.mapDataSequence++
    this.jointLoadSequence += 1
    this.resetJourneyEvents()
    this.clearRefreshTimer()
    this.destroyMap()
  },
  methods: {
    createMapGuard() {
      const sequence = this.mapOpenSequence
      const transportNo = this.detail.waybillNo || this.detail.transportNo
      return () => !this._isDestroyed && sequence === this.mapOpenSequence && transportNo === (this.detail.waybillNo || this.detail.transportNo)
    },
    resizeDialogMap() { if (this.map && typeof this.map.resize === 'function') this.map.resize() },
    handleStatusLabel,
    handleStatusTag,
    openEventPopup(type) {
      if (this.shareMode) return
      this.eventPopupType = type
      if (type === 'all' || type === 'parking') this.resolveParkingAddresses()
      if (type === 'all' && !this.journeyLoading && (!this.journeyLoaded || this.journeyCursor)) this.loadJourneyEvents({ all: true })
    },
    focusPopupEvent(item) {
      if (item.type === 'parking') this.focusParking(item.source)
      else if (item.type === 'journey') this.focusJourneyEvent(item.source)
      else this.focusAlarm(item.source)
      this.eventPopupType = ''
    },
    async retryEventPopup() {
      if (this.jointMode) return this.loadJointData()
      await Promise.all([this.loadAlarms(), this.eventPopupType === 'all' ? this.loadJourneyEvents({ reset: true, all: true }) : Promise.resolve()])
    },
    toggleEventPanel() {
      this.eventPopupType = ''
      this.eventPanelCollapsed = !this.eventPanelCollapsed
      this.$nextTick(() => { if (this.map && typeof this.map.resize === 'function') this.map.resize() })
    },
    async setEventPanelView(view) {
      this.eventPanelView = view
      if (!this.shareMode && view === 'overview') this.sidebarTab = 'info'
      if (view === 'detail') {
        this.resolveParkingAddresses()
        this.resolveAlarmAddresses()
        if (!this.journeyLoaded || this.journeyCursor || Number(this.detail.transportStatus) === 1) await this.loadJourneyEvents({ reset: true, all: true })
      }
    },
    openTrackExport() {
      const playback = this.$refs.playback
      if (this.loading || !playback || typeof playback.openExportDialog !== 'function') {
        this.$message.warning(this.loading ? '轨迹正在加载，请稍后再导出' : '暂无有效轨迹，无法导出')
        return false
      }
      return playback.openExportDialog()
    },
    focusPanelEvent(event) {
      if (event.category === 'parking') this.focusParking(event.source)
      else if (event.category === 'alarm') this.focusAlarm(event.source)
      else this.focusJourneyEvent(event.source)
    },
    formatPanelSpeed(value) { return value === null ? '-' : `${Number(value).toFixed(2).replace(/\.00$/, '')} km/h` },
    formatPanelDuration(value) {
      const seconds = Math.max(0, Math.floor(Number(value) || 0))
      const days = Math.floor(seconds / 86400)
      const hours = Math.floor(seconds % 86400 / 3600)
      return `${days ? `${days}天` : ''}${hours ? `${hours}小时` : ''}${Math.floor(seconds % 3600 / 60)}分${seconds % 60}秒`
    },
    nodeCoordinateText(value) { return formatLonLat(wgs84LonLatToGcj02Point(value)) },
    async openMap() {
      if (this.mapOpened || this._isDestroyed) return
      this.mapOpenSequence++
      const current = this.createMapGuard()
      this.mapOpened = true
      this.loading = true
      try {
        if (this.jointMode) {
          this.planRouteVisible = false
          await this.loadJointData()
          if (!current()) return
          await this.initMap()
          if (!current()) return
          this.renderMap()
          this.startRefreshTimer()
          return
        }
        await Promise.all([this.loadMapData(), this.loadAlarms(), this.loadJourneyEvents({ reset: true, all: true })])
        if (!current()) return
        await this.loadMonitorTrack({ reset: true, onePage: true })
        if (!current()) return
        await this.initMap()
        if (!current()) return
        await this.loadRoutePoints()
        if (!current()) return
        this.renderMap()
        this.syncEstimateRoute()
        await this.loadMonitorTrack()
        if (!current()) return
        this.startRefreshTimer()
        if (this.initialView === 'position' && this.currentTrack) {
          const point = wgs84LonLatToGcj02Point(`${this.currentTrack.longitude},${this.currentTrack.latitude}`)
          if (point) {
            this.map.setZoomAndCenter(13, point)
            this.showCurrentInfo(this.currentTrack, point)
          }
        }
        if (this.isShareH5Viewport()) {
          this.$nextTick(() => {
            window.setTimeout(() => {
              if (current() && !this.trajectoryPlaybackActive) this.focusAll()
            }, 100)
          })
        }
      } catch (e) {
        if (current()) this.$message.error(e.msg || e.message || '运单地图加载失败')
      } finally {
        if (current()) this.loading = false
      }
    },
    closeMap() {
      this.mapOpenSequence++
      this.mapDataSequence++
      this.jointLoadSequence += 1
      this.resetJourneyEvents()
      this.jointEntries = []
      this.mapOpened = false
      this.loading = false
      this.destroyMap()
      this.fullscreen = false
      this.routePlan = null
      this.routePoints = []
      this.mapFences = []
      this.monitorData = null
      this.trackCursor = null
      this.backfillCursor = null
      this.trackHasMore = false
      this.backfillHasMore = false
      this.backfillBaselinePending = false
      this.alarms = []
      this.alarmLoadError = ''
      this.alarmLoadSequence++
      this.alarmLoading = false
      this.parkingAddressMap = {}
      this.parkingMinMinutes = 10
      this.parkingMinInput = 10
      this.alarmAddressMap = {}
      this.sidebarTab = 'info'
      this.eventPanelView = 'overview'
      this.eventPanelCollapsed = true
      this.eventPopupType = ''
      this.eventCategories = ['parking', 'journey', 'alarm']
      this.planRouteVisible = true
      this.actualTrajectoryVisible = true
      this.estimateRouteVisible = false
      this.toolsMenuOpen = false
      this.showSatellite = false
      this.hideParkingMarkers = false
      this.hideEndpointMarkers = true
      this.reportPointsOnly = false
      this.refreshingTrack = false
      this.trajectoryPlaybackActive = false
      this.playbackRangeClipped = false
      this.playbackTrackSnapshot = []
      this.playbackExpandedNum = 3
      this.h5InfoCollapsed = false
      this.trajectoryDisplayOverride = null
      this.vehicleInfoFollowing = false
      this.lastVehicleInfoSyncAt = 0
      this.clearRefreshTimer()
    },
    resetJourneyEvents() {
      this.journeyLoadSequence += 1
      this.journeyEvents = []
      this.journeyCursor = null
      this.journeyLoading = false
      this.journeyError = ''
      this.journeyLoaded = false
    },
    async loadJourneyEvents({ reset = false, all = false } = {}) {
      const transportNo = this.journeyWaybillNo
      if (!transportNo || !this.mapOpened || this.draftMode || this.shareMode || this.embedMode || this.jointMode || (this.journeyLoading && !reset)) return
      if (this.journeyLoaded && !this.journeyCursor && !reset) return
      const sequence = ++this.journeyLoadSequence
      const mapCurrent = this.createMapGuard()
      const current = () => sequence === this.journeyLoadSequence && this.mapOpened && mapCurrent()
      if (reset) {
        this.journeyCursor = null
        this.journeyLoaded = false
      }
      let cursor = this.journeyCursor
      const events = new Map((reset ? [] : this.journeyEvents).map(event => [event.eventId, event]))
      this.journeyLoading = true
      this.journeyError = ''
      try {
        do {
          const res = await getJourneyNodes({ transportNo, cursor, limit: 100 })
          if (!current()) return
          if (!isRequestSuccess(res)) throw new Error(res.msg || '行程事件读取失败')
          const page = res.data || {}
          if (page.nextCursor && page.nextCursor === cursor) throw new Error('行程事件分页游标未前进')
          ;(page.items || []).forEach(event => events.set(event.eventId, event))
          cursor = page.nextCursor || null
        } while (all && cursor)
        this.journeyEvents = Array.from(events.values())
        this.journeyCursor = cursor
        this.journeyLoaded = true
      } catch (error) {
        if (current()) this.journeyError = error.msg || error.message || '行程事件读取失败'
      } finally {
        if (sequence === this.journeyLoadSequence) this.journeyLoading = false
      }
    },
    journeyEventPoint(event) {
      if (!event || [event.longitude, event.latitude].some(value => value == null || String(value).trim() === '')) return null
      return wgs84LonLatToGcj02Point(`${event.longitude},${event.latitude}`)
    },
    focusJourneyEvent(event) {
      const point = this.journeyEventPoint(event)
      if (!point || !this.map) return
      this.map.setZoom(15)
      this.map.setCenter(point)
    },
    changeParkingMinMinutes(value) {
      const minutes = Number(value)
      if (value == null || String(value).trim() === '' || !Number.isInteger(minutes) || minutes < 0) {
        // 等输入控件提交本次值后再回填，避免同一轮更新被合并而留下非法显示。
        this.$nextTick(() => { this.parkingMinInput = this.parkingMinMinutes })
        return
      }
      this.parkingMinInput = minutes
      if (minutes === this.parkingMinMinutes) return
      this.parkingMinMinutes = minutes
      if (this.sidebarTab === 'parking' || (!this.shareMode && this.eventPanelView === 'detail')) this.$nextTick(() => this.resolveParkingAddresses())
    },
    parkingDurationTotal(points) {
      return points.reduce((sum, item) => sum + item.duration, 0)
    },
    async loadMapData() {
      const sequence = ++this.mapDataSequence
      const detail = this.detail
      const current = () => !this._isDestroyed && sequence === this.mapDataSequence && detail === this.detail
      if (this.shareMode) {
        this.routePlan = detail.routeId ? (this.initialRoutePlan || null) : null
        this.mapFences = this.initialFences || []
        return
      }
      const routeId = this.detail.routeId
      const fenceCodes = String(this.detail.waypointFenceCode || this.detail.waypointFenceCodes || '').split(',').map(item => item.trim()).filter(Boolean)
      let routePlan = routeId ? (this.initialRoutePlan || this.detail.route || null) : null
      let suppliedFences = this.initialFences.length ? this.initialFences : (this.detail.fences || this.fences)
      const transportNo = this.draftMode ? '' : (this.detail.waybillNo || this.detail.transportNo)
      const needsRouteGeometry = routeId && !(routePlan && routePlan.routeList)
      // 已有绑定从运单详情读取，避免正常资料列表过滤掉禁用资料。
      if (transportNo && (needsRouteGeometry || (!suppliedFences.length && fenceCodes.length))) {
        const res = await getWaybillDetail({ transportNo })
        if (!current()) return
        if (!isRequestSuccess(res)) throw new Error(res.msg || '运单绑定资料加载失败')
        const data = res.data || {}
        if (needsRouteGeometry) routePlan = data.route || null
        if (!suppliedFences.length) suppliedFences = data.fences || []
      } else if (needsRouteGeometry) {
        const res = await getRouteDetail(routeId)
        if (!current()) return
        if (!isRequestSuccess(res)) throw new Error(res.msg || '规划路线加载失败')
        routePlan = res.data || null
      }
      if (!current()) return
      this.routePlan = routeId ? routePlan : null
      this.mapFences = this.filterFences(suppliedFences, fenceCodes)
    },
    async loadJointData() {
      const sequence = ++this.jointLoadSequence
      this.jointLoadError = ''
      if (!this.jointEntries.length) this.jointEntries = this.jointDetails.map(data => ({ waybill: data.waybill || data, monitorData: { monitor: data.monitor || null, tracks: [] }, events: [], alarms: [] }))
      try {
        const entries = await Promise.all(this.jointDetails.map(async data => {
          const waybill = data.waybill || data
          const transportNo = waybill.waybillNo || waybill.transportNo
          const entry = { waybill, monitorData: { monitor: data.monitor || null, tracks: [] }, events: [], alarms: [] }
          await Promise.all([
            (async () => {
              const tracks = new Map()
              let afterTime = null
              let afterBackfillId = null
              let more = true
              while (more) {
                const params = { transportNo, limit: 2000 }
                if (afterTime !== null) params.afterTime = afterTime
                if (afterBackfillId !== null) params.afterBackfillId = afterBackfillId
                const res = await getMonitorTrack(params)
                if (!isRequestSuccess(res)) throw new Error(res.msg || `${transportNo} 轨迹读取失败`)
                const page = res.data || {}
                ;(page.tracks || []).forEach(track => tracks.set(this.trackIdentity(track), track))
                entry.monitorData = { ...entry.monitorData, ...page, monitor: page.monitor || entry.monitorData.monitor }
                const nextTime = page.nextCursor == null ? afterTime : Number(page.nextCursor)
                const nextBackfill = page.nextBackfillCursor == null ? afterBackfillId : Number(page.nextBackfillCursor)
                // 首屏只提供补报基线，仍须追读一次以覆盖首屏查询期间提交的补报。
                more = page.hasMore === true || page.backfillHasMore === true || (afterBackfillId === null && nextBackfill !== null)
                if (more && nextTime === afterTime && nextBackfill === afterBackfillId) throw new Error(`${transportNo} 轨迹分页游标未前进`)
                afterTime = nextTime
                afterBackfillId = nextBackfill
              }
              entry.monitorData.tracks = Array.from(tracks.values()).sort((a, b) => this.trackTimestamp(a) - this.trackTimestamp(b))
            })(),
            (async () => {
              let cursor = null
              do {
                const res = await getJourneyNodes({ transportNo, cursor, limit: 100 })
                if (!isRequestSuccess(res)) throw new Error(res.msg || `${transportNo} 行程事件读取失败`)
                const page = res.data || {}
                entry.events.push(...(page.items || []))
                if (page.nextCursor && page.nextCursor === cursor) throw new Error(`${transportNo} 行程事件分页游标未前进`)
                cursor = page.nextCursor
              } while (cursor)
            })(),
            (async () => {
              let pageNumber = 1
              let more = true
              while (more) {
                const res = await getAlarmList({ transportNo, page: pageNumber, limit: 100 })
                if (!isRequestSuccess(res)) throw new Error(res.msg || `${transportNo} 告警读取失败`)
                const page = res.data || {}
                const items = page.items || []
                entry.alarms.push(...items.map(item => ({ ...item, waybillNo: transportNo })))
                more = items.length === 100 && (page.total == null || entry.alarms.length < Number(page.total))
                pageNumber += 1
              }
            })()
          ])
          return entry
        }))
        if (sequence !== this.jointLoadSequence) return
        this.jointEntries = entries
        this.jointNow = Date.now()
        this.alarms = entries.flatMap(entry => entry.alarms).sort((a, b) => this.trackTimestamp({ locationTime: a.alarmTime || a.createTime }) - this.trackTimestamp({ locationTime: b.alarmTime || b.createTime }))
        await this.renderLoadedTrackPage()
      } catch (error) {
        if (sequence === this.jointLoadSequence) this.jointLoadError = error.message || '合并回放加载失败'
        throw error
      }
    },
    async loadMonitorTrack({ reset = false, onePage = false, force = false } = {}) {
      if (this.draftMode || this._isDestroyed) return
      const current = this.createMapGuard()
      this.monitorNow = Date.now()
      const transportNo = this.detail.waybillNo || this.detail.transportNo
      if (!transportNo) {
        this.monitorData = null
        return
      }
      if (reset) {
        this.monitorLoadError = ''
        if (this.shareMode) {
          this.setInitialShareMonitorData(this.initialMonitorData)
          if (onePage && this.monitorData) {
            await this.renderLoadedTrackPage()
            return
          }
        } else {
          this.monitorData = this.initialMonitorData
            ? { monitor: this.initialMonitorData, tracks: [], currentTrack: null }
            : null
          this.trackCursor = null
          this.backfillCursor = null
          this.trackHasMore = true
          this.backfillHasMore = true
          this.backfillBaselinePending = false
        }
      }
      let shouldFetch = reset || force || this.trackHasMore || this.backfillHasMore || this.backfillBaselinePending
      let pageCount = 0
      while (shouldFetch && pageCount < 100) {
        const params = { transportNo, limit: 2000 }
        if (this.trackCursor !== null) params.afterTime = this.trackCursor
        if (this.backfillCursor !== null) params.afterBackfillId = this.backfillCursor
        let page
        if (this.shareMode) {
          if (typeof this.shareRefreshHandler !== 'function') throw new Error('分享轨迹读取方式未配置')
          page = await this.shareRefreshHandler(params)
        } else {
          try {
            const res = await getMonitorTrack(params)
            if (!isRequestSuccess(res)) throw new Error(res && (res.msg || res.message))
            page = res.data || {}
          } catch (error) {
            if (!current()) return
            this.monitorLoadError = error.msg || error.message || '监控数据读取失败'
            throw error
          }
        }
        if (!current()) return
        this.monitorLoadError = ''
        this.mergeTrackPage(page || {})
        pageCount += 1
        await this.renderLoadedTrackPage()
        if (!current()) return
        if (onePage) break
        shouldFetch = this.trackHasMore || this.backfillHasMore || this.backfillBaselinePending
      }
    },
    setInitialShareMonitorData(data) {
      this.monitorData = data || null
      this.trackCursor = data && data.nextCursor !== null && data.nextCursor !== undefined ? Number(data.nextCursor) : null
      this.backfillCursor = data && data.nextBackfillCursor !== null && data.nextBackfillCursor !== undefined ? Number(data.nextBackfillCursor) : null
      this.trackHasMore = Boolean(data && data.hasMore)
      this.backfillHasMore = Boolean(data && data.backfillHasMore)
      this.backfillBaselinePending = this.backfillCursor !== null
    },
    async refreshWaybillTrack() {
      const transportNo = this.detail.waybillNo || this.detail.transportNo
      if (!transportNo || this.refreshingTrack || this.draftMode || this._isDestroyed) return
      const current = this.createMapGuard()
      this.refreshingTrack = true
      try {
        const payload = { transportNo, limit: 2000 }
        if (this.trackCursor !== null) payload.afterTime = this.trackCursor
        if (this.backfillCursor !== null) payload.afterBackfillId = this.backfillCursor
        const res = await refreshMonitorTrack(payload)
        if (!current()) return
        if (!isRequestSuccess(res)) throw new Error(res && (res.msg || res.message))
        this.monitorLoadError = ''
        this.mergeTrackPage(res.data || {})
        await this.renderLoadedTrackPage()
        if (!current()) return
        await this.loadMonitorTrack()
        if (!current()) return
        if (this.monitor && this.monitor.syncStatus === 'FAILED') throw new Error('位置查询失败，请稍后重试')
        if (this.sidebarTab === 'parking') this.resolveParkingAddresses()
        if (this.beforePlannedDeparture && !this.currentTrack) this.$message.info('未到计划出发时间，当前仅显示规划路线')
        else if (this.currentTrack && this.validPlaybackTracks.length < 2) this.$message.info('已刷新车辆位置，暂无可回放的本单轨迹')
        else if (!this.currentTrack) this.$message.info('尚未获取到车辆定位和本单轨迹')
        else this.$message.success('已刷新最新位置和轨迹')
      } catch (e) {
        if (!current()) return
        this.monitorLoadError = e.msg || e.message || '刷新轨迹失败'
        this.$message.error(e.msg || e.message || '刷新轨迹失败')
      } finally {
        if (current()) this.refreshingTrack = false
      }
    },
    async refreshSharedWaybillTrack() {
      if (this.refreshingTrack || this._isDestroyed || typeof this.shareRefreshHandler !== 'function') return
      const current = this.createMapGuard()
      this.refreshingTrack = true
      try {
        const monitorData = await this.shareRefreshHandler({
          transportNo: this.detail.waybillNo || this.detail.transportNo,
          limit: 2000
        })
        if (!current()) return
        this.setInitialShareMonitorData(monitorData)
        await this.renderLoadedTrackPage()
        if (!current()) return
        await this.loadMonitorTrack()
        if (!current()) return
        if (this.sidebarTab === 'parking') this.resolveParkingAddresses()
        this.$message.success('已加载最新已存储轨迹')
      } catch (e) {
        if (current()) this.$message.error(e.msg || e.message || '刷新轨迹失败')
      } finally {
        if (current()) this.refreshingTrack = false
      }
    },
    mergeTrackPage(data) {
      this.monitorNow = Date.now()
      const currentData = this.monitorData || {}
      const trackMap = new Map()
      ;(Array.isArray(currentData.tracks) ? currentData.tracks : []).forEach((track) => {
        const key = this.trackIdentity(track)
        if (key) trackMap.set(key, track)
      })
      ;(Array.isArray(data.tracks) ? data.tracks : []).forEach((track) => {
        const key = this.trackIdentity(track)
        if (key) trackMap.set(key, track)
      })
      const tracks = Array.from(trackMap.values()).sort((left, right) => this.trackTimestamp(left) - this.trackTimestamp(right))
      this.monitorData = {
        ...currentData,
        monitor: { ...(currentData.monitor || {}), ...(data.monitor || {}) },
        tracks,
        currentTrack: data.currentTrack || currentData.currentTrack || (tracks.length ? tracks[tracks.length - 1] : null)
      }
      // 只有首次收到基线需要额外追读；已有基线的响应即消费此次追读机会。
      this.backfillBaselinePending = this.backfillCursor === null && data.nextBackfillCursor !== null && data.nextBackfillCursor !== undefined
      if (data.nextCursor !== null && data.nextCursor !== undefined) this.trackCursor = Number(data.nextCursor)
      if (data.nextBackfillCursor !== null && data.nextBackfillCursor !== undefined) this.backfillCursor = Number(data.nextBackfillCursor)
      this.trackHasMore = data.hasMore === true
      this.backfillHasMore = data.backfillHasMore === true
    },
    async renderLoadedTrackPage() {
      await this.$nextTick()
      if (this.mapOpened && this.$refs.trajectory && this.$refs.trajectory.mapInstance) {
        await this.renderTrajectoryOverlays()
      }
      if (this.mapOpened && !this.shareMode && this.eventPanelView === 'detail') this.resolveParkingAddresses()
    },
    async loadAlarms() {
      if (this.draftMode || this._isDestroyed) return
      const sequence = ++this.alarmLoadSequence
      const mapCurrent = this.createMapGuard()
      const current = () => sequence === this.alarmLoadSequence && mapCurrent()
      this.alarmLoadError = ''
      this.alarmLoading = false
      if (this.shareMode) {
        this.alarms = this.initialAlarms || []
        return
      }
      if (this.embedMode && this.initialAlarms.length) {
        this.alarms = this.initialAlarms
        return
      }
      const transportNo = this.detail.waybillNo || this.detail.transportNo
      if (!transportNo) {
        this.alarms = []
        return
      }
      this.alarmLoading = true
      try {
        for (let attempt = 0; attempt < 2; attempt++) {
          const alarms = new Map()
          let pageNumber = 1, total = null, more, changed = false
          do {
            const res = await getAlarmList({ transportNo, page: pageNumber, limit: 100 })
            if (!current()) return
            if (!isRequestSuccess(res)) throw new Error(res.msg || '告警读取失败')
            const page = res.data || {}
            const items = page.items || []
            if (pageNumber === 1) total = page.total == null ? null : Number(page.total)
            else if (total !== null && page.total != null && total !== Number(page.total)) { changed = true; break }
            items.forEach((alarm, index) => alarms.set(alarm.alarmNo || alarm.id || `${pageNumber}:${index}`, alarm))
            more = !this.embedMode && items.length === 100 && (total === null || pageNumber * 100 < total)
            pageNumber++
          } while (more)
          // 偏移分页遇到新增记录会移动页边界，最多完整重读一次，避免重复或漏掉最新记录。
          if (!this.embedMode && (changed || (total !== null && alarms.size !== total))) {
            if (attempt === 0) continue
            throw new Error('告警列表在读取期间发生变化，请重试')
          }
          this.alarms = Array.from(alarms.values())
          break
        }
        if (this.sidebarTab === 'alarm' || (!this.shareMode && this.eventPanelView === 'detail')) this.resolveAlarmAddresses()
      } catch (e) {
        if (current()) this.alarmLoadError = e.msg || e.message || '告警读取失败'
      } finally {
        if (current()) this.alarmLoading = false
      }
    },
    startRefreshTimer() {
      this.clearRefreshTimer()
      if (this.draftMode || this._isDestroyed || !this.mapOpened) return
      const current = this.createMapGuard()
      if (this.jointMode) {
        if (!this.jointDetails.some(data => Number((data.waybill || data).transportStatus) === 1)) return
        this.refreshTimer = window.setInterval(() => { if (current()) this.loadJointData().catch(() => {}) }, 30000)
        return
      }
      if (this.shareMode || ![0, 1].includes(Number(this.detail.transportStatus))) return
      this.refreshTimer = window.setInterval(async () => {
        if (!current()) return
        this.monitorNow = Date.now()
        if (Number(this.detail.transportStatus) !== 1) return
        try {
          await Promise.all([this.loadMonitorTrack({ force: true }), this.loadAlarms(), this.loadJourneyEvents({ reset: true, all: true })])
          if (current()) this.$nextTick(() => { if (current()) this.renderTrajectoryOverlays().catch(() => {}) })
        } catch (e) {
          // 单次刷新失败不影响已打开的地图。
        }
      }, 30000)
    },
    clearRefreshTimer() {
      if (this.refreshTimer) window.clearInterval(this.refreshTimer)
      this.refreshTimer = null
    },
    filterFences(fences, codes) {
      if (!codes.length) return []
      return (fences || []).filter(item => codes.includes(item.fenceCode))
    },
    async initMap() {
      if (this._isDestroyed) return
      const current = this.createMapGuard()
      this.clearExternalOverlays()
      const trajectory = await this.waitForTrajectoryMap()
      if (!current()) return
      await this.waitForTrajectoryRender(trajectory)
      if (!current()) return
      await loadAmapSDK(['AMap.Driving'])
      if (!current()) return
      this.map = trajectory.mapInstance
      this.infoWindow = new AMap.InfoWindow({ offset: new AMap.Pixel(0, -24) })
      this.syncTrajectoryToolStates(trajectory)
      if (trajectory.infoWindow) trajectory.infoWindow.close()
    },
    async waitForTrajectoryMap() {
      const current = this.createMapGuard()
      for (let count = 0; count < 80; count++) {
        if (!current()) return null
        const trajectory = this.$refs.trajectory
        if (trajectory && trajectory.mapInstance && trajectory.AMap) return trajectory
        await new Promise(resolve => window.setTimeout(resolve, 50))
      }
      throw new Error('运输轨迹地图初始化失败')
    },
    async waitForTrajectoryRender(trajectory) {
      const current = this.createMapGuard()
      const expectedCount = this.trajectoryTrackData.traceList.length
      if (!expectedCount) return
      for (let count = 0; count < 80; count++) {
        if (!current()) return
        if (trajectory.pathSimplifierIns && trajectory.traceList.length === expectedCount) return
        await new Promise(resolve => window.setTimeout(resolve, 50))
      }
    },
    async renderTrajectoryOverlays() {
      if (this._isDestroyed) return
      const current = this.createMapGuard()
      const trajectory = await this.waitForTrajectoryMap()
      if (!current()) return
      await this.waitForTrajectoryRender(trajectory)
      if (!current()) return
      this.map = trajectory.mapInstance
      this.syncTrajectoryToolStates(trajectory)
      this.renderMap()
      this.syncEstimateRoute()
    },
    async loadRoutePoints() {
      if (this.jointMode) return
      // 规划仅使用本单保存的有效路线，不能按节点临时补画另一条路线。
      this.routePoints = this.detail.routeId ? wgs84RouteToGcj02(this.routePlan && this.routePlan.routeList) : []
    },
    extractRoutePoints(route) {
      const points = []
      ;(route && route.steps ? route.steps : []).forEach((step) => {
        (step.path || []).forEach((point) => {
          const lng = typeof point.getLng === 'function' ? point.getLng() : point.lng
          const lat = typeof point.getLat === 'function' ? point.getLat() : point.lat
          if (Number.isFinite(Number(lng)) && Number.isFinite(Number(lat))) points.push([Number(lng), Number(lat)])
        })
      })
      return points.filter((point, index) => !index || point[0] !== points[index - 1][0] || point[1] !== points[index - 1][1])
    },
    renderMap(fitView = true) {
      if (!this.map) return
      this.clearExternalOverlays()
      const overlays = []
      if (this.planRouteVisible) this.renderRoute(overlays)
      if (!this.trajectoryPlaybackActive) this.renderCurrentMarker(overlays)
      this.renderFences(overlays)
      this.renderNodeMarkers(overlays)
      this.externalOverlays = overlays
      // 事件侧栏由 flex 独立占位，地图仅为内部左侧工具留边，不能再计算一次侧栏宽度。
      const padding = this.isShareH5Viewport() ? [32, 32, 150, 32] : (this.shareMode ? [55, 55, 55, 410] : [70, 55, 55, 170])
      if (fitView && overlays.length) this.map.setFitView(overlays, false, padding)
    },
    renderRoute(overlays) {
      if (this.routePoints.length < 2) return
      const line = new AMap.Polyline({
        path: this.routePoints,
        strokeColor: '#f5222d',
        strokeWeight: 5,
        strokeOpacity: .85,
        strokeStyle: 'dashed',
        strokeDasharray: [10, 8],
        lineJoin: 'round',
        lineCap: 'round',
        zIndex: 20
      })
      line.setMap(this.map)
      overlays.push(line)
    },
    renderCurrentMarker(overlays) {
      const current = this.currentTrack
      const currentPoint = current && wgs84LonLatToGcj02Point(`${current.longitude},${current.latitude}`)
      if (!currentPoint) return
      const marker = new AMap.Marker({
        position: currentPoint,
        icon: new AMap.Icon({ size: new AMap.Size(40, 40), image: carIcon, imageSize: new AMap.Size(40, 40) }),
        offset: new AMap.Pixel(-20, -20),
        angle: Number.isFinite(Number(current.direction)) ? Number(current.direction) : 0,
        zIndex: 120
      })
      marker.on('click', () => this.showCurrentInfo(current, currentPoint))
      marker.setMap(this.map)
      overlays.push(marker)
    },
    renderNodeMarkers(overlays) {
      this.nodes.forEach((node, index) => {
        const point = wgs84LonLatToGcj02Point(node.lonLat)
        if (!point) return
        const marker = this.createNodeMarker(node, index, point)
        marker.on('click', () => this.showNodeInfo(node, point))
        marker.setMap(this.map)
        overlays.push(marker)
      })
    },
    createNodeMarker(node, index, point) {
      if (node.type === 'origin' || node.type === 'target') {
        const image = node.type === 'origin' ? loadIcon : unloadIcon
        return new AMap.Marker({
          position: point,
          icon: new AMap.Icon({ size: new AMap.Size(30, 41), image, imageSize: new AMap.Size(30, 41) }),
          offset: new AMap.Pixel(-15, -41),
          zIndex: 100
        })
      }
      const style = NODE_STYLE.stopover
      return new AMap.Marker({
        position: point,
        content: `<div style="width:30px;height:30px;border-radius:15px;background:${style.color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);color:#fff;text-align:center;line-height:30px;font-size:12px;font-weight:600">${index}</div>`,
        offset: new AMap.Pixel(-15, -15),
        zIndex: 100
      })
    },
    toggleFullscreen() {
      if (this.fullPage) {
        const target = this.$el
        const toggle = !document.fullscreenElement && target && target.requestFullscreen ? target.requestFullscreen() : document.exitFullscreen()
        Promise.resolve(toggle).catch(() => {}).finally(() => {
          this.$nextTick(() => { if (this.map && typeof this.map.resize === 'function') this.map.resize() })
        })
        return
      }
      this.$el.dispatchEvent(new Event('dialog-window-toggle'))
    },
    toggleEstimateRoute() {
      this.estimateRouteVisible = !this.estimateRouteVisible
      this.syncEstimateRoute()
    },
    syncEstimateRoute() {
      const trajectory = this.$refs.trajectory
      if (!trajectory || typeof trajectory.setEstimateRouteOverlay !== 'function') return
      trajectory.setEstimateRouteOverlay({
        visible: this.estimateRouteVisible && this.estimateRoute.length > 1,
        estimateRoute: this.estimateRoute
      })
    },
    getTrajectory() { return this.$refs.trajectory || null },
    initPlaybackPanel() {
      const playback = this.$refs.playback
      if (playback && typeof playback.resetTrajectoryPlayback === 'function') playback.resetTrajectoryPlayback()
    },
    async createWaybillShareLink(options = {}) {
      if (this.draftMode) throw new Error('请先保存运单后再分享')
      const transportNo = this.detail.waybillNo || this.detail.transportNo
      if (!transportNo) throw new Error('运单号不能为空')
      const scope = options.scope === 'CURRENT_POSITION' ? 'CURRENT_POSITION' : 'FULL_TRIP'
      const expiresInHours = Number(options.expiresInHours) || 72
      const res = await createWaybillShare({ transportNo, scope, expiresInHours })
      if (!isRequestSuccess(res)) throw new Error((res && (res.msg || res.message)) || '创建分享链接失败')
      const shareId = res.data && res.data.shareId
      if (!shareId) throw new Error('创建分享链接失败')
      const route = this.$router.resolve({
        name: 'LingDongWaybillShareMapName',
        params: { shareId }
      })
      return {
        url: new URL(route.href, window.location.origin).href,
        shareId,
        scope: (res.data && res.data.scope) || scope,
        expiresAt: res.data && res.data.expiresAt
      }
    },
    async revokeWaybillShareLink(shareId) {
      if (this.draftMode) throw new Error('请先保存运单后再管理分享')
      if (!shareId) throw new Error('分享标识不能为空')
      const res = await revokeWaybillShare({ shareId })
      if (!isRequestSuccess(res)) throw new Error((res && (res.msg || res.message)) || '撤销分享失败')
      return true
    },
    prepareCaptureLayout() {
      this.focusAll()
    },
    handleLocateOnMap(payload) {
      const trajectory = this.getTrajectory()
      if (trajectory && typeof trajectory.locateOnMap === 'function') trajectory.locateOnMap(payload)
    },
    updateTrajectoryDisplayOverride(payload) {
      this.trajectoryDisplayOverride = payload || null
    },
    async handlePlaybackControl(payload) {
      if (!payload || !payload.type) return
      const shouldShowPlaybackVehicle = payload.type === 'play' || (payload.type === 'seek' && !payload.rangeReset)
      if (shouldShowPlaybackVehicle && !this.trajectoryPlaybackActive) {
        if (!this.playbackRangeClipped) this.playbackTrackSnapshot = this.tracks.slice()
        this.trajectoryPlaybackActive = true
        await this.$nextTick()
        this.renderMap(false)
      }
      const trajectory = this.getTrajectory()
      if (!trajectory || typeof trajectory.onPathNavControl !== 'function') return
      trajectory.onPathNavControl(payload)
      if (payload.type === 'play' && trajectory.navg && typeof trajectory.navg.getPosition === 'function') this.focusPlaybackVehicle(trajectory.navg.getPosition())
      const playback = this.$refs.playback
      if (payload.type === 'pause' && playback && playback.playbackCompletedInRange) {
        this.$nextTick(() => this.stopTrajectoryPlayback())
      }
    },
    handlePlaybackRangeChange(payload) {
      if (!payload) return
      const clipped = !isFullPlaybackClip(payload.startRatio, payload.endRatio)
      // 选区绑定选取时的轨迹，避免实时新增点使时间范围复位或漂移。
      if (clipped && !this.playbackTrackSnapshot.length) this.playbackTrackSnapshot = this.tracks.slice()
      this.playbackRangeClipped = clipped
      if (!clipped && !this.trajectoryPlaybackActive) this.playbackTrackSnapshot = []
      const trajectory = this.getTrajectory()
      if (trajectory && typeof trajectory.applyPlaybackRangeClip === 'function') {
        trajectory.applyPlaybackRangeClip(payload)
      }
    },
    handlePlaybackCollapse(num) {
      this.playbackExpandedNum = Number(num) || 3
      this.$nextTick(() => {
        if (this.map && typeof this.map.resize === 'function') this.map.resize()
      })
    },
    isShareH5Viewport() {
      return this.shareMode && typeof window !== 'undefined' && window.innerWidth <= 768
    },
    focusPlaybackVehicle(position) {
      if (!this.isShareH5Viewport() || !this.map || !position) return
      const longitude = Number(position.lon != null ? position.lon : position.lng)
      const latitude = Number(position.lat)
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return
      if (typeof this.map.setZoomAndCenter === 'function') this.map.setZoomAndCenter(13, [longitude, latitude])
      else {
        this.map.setZoom(13)
        this.map.setCenter([longitude, latitude])
      }
    },
    setH5TrackDetails(num) {
      if (!this.isShareH5Viewport()) return
      this.playbackExpandedNum = num
      this.$nextTick(() => {
        if (this.map && typeof this.map.resize === 'function') this.map.resize()
      })
    },
    toggleH5InfoPanel() {
      if (!this.isShareH5Viewport()) return
      this.h5InfoCollapsed = !this.h5InfoCollapsed
      this.$nextTick(() => {
        window.setTimeout(() => {
          if (!this.map || typeof this.map.resize !== 'function') return
          this.map.resize()
          if (this.h5InfoCollapsed) this.focusAll()
        }, 0)
      })
    },
    handleTrajectoryNavProgress(payload) {
      const playback = this.$refs.playback
      if (playback && typeof playback.syncFromPathNavigatorRatio === 'function' && payload) {
        playback.syncFromPathNavigatorRatio(payload.ratio, payload.navPos)
      }
      if (!this.trajectoryPlaybackActive || !this.vehicleInfoFollowing || !payload || !payload.navPos) return
      const now = Date.now()
      if (now - this.lastVehicleInfoSyncAt < 120) return
      this.lastVehicleInfoSyncAt = now
      const sourceTracks = this.playbackSourceTracks
      const index = Math.min(sourceTracks.length - 1, Math.max(0, Math.round((sourceTracks.length - 1) * (Number(payload.ratio) || 0))))
      this.showTrajectoryVehicleInfo(sourceTracks[index], [payload.navPos.lon, payload.navPos.lat])
    },
    handleTrajectoryNavigatorVehicleClick(payload) {
      if (!payload || !payload.navPos) return
      this.vehicleInfoFollowing = true
      const sourceTracks = this.playbackSourceTracks
      const index = Number.isFinite(Number(payload.trackIndex))
        ? Math.min(sourceTracks.length - 1, Math.max(0, Number(payload.trackIndex)))
        : Math.min(sourceTracks.length - 1, Math.max(0, Math.round((sourceTracks.length - 1) * (Number(payload.ratio) || 0))))
      this.showTrajectoryVehicleInfo(sourceTracks.length ? sourceTracks[index] : this.currentTrack, [payload.navPos.lon, payload.navPos.lat])
    },
    stopTrajectoryPlayback() {
      if (!this.trajectoryPlaybackActive) return
      this.trajectoryPlaybackActive = false
      if (!this.playbackRangeClipped) this.playbackTrackSnapshot = []
      this.$nextTick(() => {
        this.renderMap(false)
        if (this.vehicleInfoFollowing && this.currentTrack) {
          const point = wgs84LonLatToGcj02Point(`${this.currentTrack.longitude},${this.currentTrack.latitude}`)
          this.showTrajectoryVehicleInfo(this.currentTrack, point)
        }
      })
    },
    syncTrajectoryToolStates(trajectory = this.getTrajectory()) {
      if (!trajectory) return
      if (typeof trajectory.setEndpointMarkersHidden === 'function') trajectory.setEndpointMarkersHidden(this.hideEndpointMarkers)
      if (typeof trajectory.setParkingMarkersHidden === 'function') trajectory.setParkingMarkersHidden(this.hideParkingMarkers)
      if (typeof trajectory.setPathLineOnlyHidden === 'function') trajectory.setPathLineOnlyHidden(this.reportPointsOnly || !this.actualTrajectoryVisible)
    },
    async onToolbarAction(payload) {
      if (!payload || !payload.type) return
      const trajectory = this.getTrajectory()
      switch (payload.type) {
        case 'toggleToolsMenu':
          this.toolsMenuOpen = !this.toolsMenuOpen
          break
        case 'toggleHideParking':
          if (trajectory && typeof trajectory.setParkingMarkersHidden === 'function') {
            this.hideParkingMarkers = !this.hideParkingMarkers
            trajectory.setParkingMarkersHidden(this.hideParkingMarkers)
            if (!this.hideParkingMarkers) this.resolveParkingAddresses()
          }
          break
        case 'toggleHideEndpoints':
          if (trajectory && typeof trajectory.setEndpointMarkersHidden === 'function') {
            this.hideEndpointMarkers = !this.hideEndpointMarkers
            trajectory.setEndpointMarkersHidden(this.hideEndpointMarkers)
          }
          break
        case 'toggleReportPoints':
          if (trajectory && typeof trajectory.setPathLineOnlyHidden === 'function') {
            this.reportPointsOnly = !this.reportPointsOnly
            trajectory.setPathLineOnlyHidden(this.reportPointsOnly)
          }
          break
        case 'ranging':
          if (trajectory && typeof trajectory.toggleRanging === 'function') await trajectory.toggleRanging()
          break
        case 'zoomIn':
          if (trajectory && typeof trajectory.zoomInMap === 'function') trajectory.zoomInMap()
          break
        case 'zoomOut':
          if (trajectory && typeof trajectory.zoomOutMap === 'function') trajectory.zoomOutMap()
          break
        case 'toggleSatellite':
          if (trajectory && typeof trajectory.toggleSatelliteLayer === 'function') this.showSatellite = trajectory.toggleSatelliteLayer()
          break
        case 'toggleFullscreen':
          this.toggleFullscreen()
          break
        case 'openGlobalMap':
          this.focusAll()
          break
        case 'colorConfig':
          if (trajectory && typeof trajectory.applyTrajectoryColorSettings === 'function') trajectory.applyTrajectoryColorSettings(payload.value)
          this.actualTrajectoryColorValue = String((payload.value && payload.value.solidColor) || getTrajectoryColorSettings().solidColor || '').split(',')[0] || '#2FAE55'
          break
        default:
          break
      }
    },
    focusAll() {
      if (!this.map) return
      if (this.infoWindow) this.infoWindow.close()
      this.map.setFitView(null, true, this.isShareH5Viewport() ? [32, 32, 150, 32] : [60, 60, 60, 60], 12)
    },
    focusNode(node) {
      const point = wgs84LonLatToGcj02Point(node.lonLat)
      if (!point || !this.map) return
      this.map.setZoom(15)
      this.map.setCenter(point)
      this.showNodeInfo(node, point)
    },
    focusAlarm(alarm) {
      this.focusAlarmWithAddress(alarm)
    },
    async focusAlarmWithAddress(alarm) {
      const point = this.alarmPoint(alarm)
      if (!point || !this.map) {
        this.$message.warning('该告警暂无可定位坐标')
        return
      }
      const address = this.alarmAddress(alarm)
      this.map.setZoom(15)
      this.map.setCenter(point)
      const rows = [
        { label: '告警时间：', value: this.formatDate(alarm.alarmTime || alarm.createTime) },
        { label: '告警位置：', value: address || '解析中…', color: '#1677ff', navigation: true },
        { label: '告警坐标：', value: this.formatAlarmLonLat(alarm) },
        { label: '告警详情：', value: alarm.alarmRemark || '-' }
      ]
      if (alarm.fenceInfo) rows.push({ label: '围栏信息：', value: alarm.fenceInfo })
      const card = {
        title: this.alarmTypeText(alarm.alarmType),
        status: '告警',
        statusColor: '#f5222d',
        rows,
        point,
        navigationAddress: address || this.alarmTypeText(alarm.alarmType)
      }
      this.openInfoCard(card)
      if (address && address !== '解析中…') return
      const infoWindow = this.infoWindow
      const resolvedAddress = await this.resolveAlarmAddress(alarm, point).catch(() => '')
      // 地址异步回填，不覆盖用户随后选择或已经关闭的详情。
      if (!infoWindow || this.infoWindow !== infoWindow || !infoWindow.getIsOpen()) return
      rows[1].value = resolvedAddress || '暂无定位地址'
      card.navigationAddress = resolvedAddress || this.alarmTypeText(alarm.alarmType)
      this.openInfoCard(card)
    },
    focusParking(parking) {
      const point = wgs84LonLatToGcj02Point(`${parking.longitude},${parking.latitude}`)
      if (!point || !this.map) return
      this.map.setZoom(15)
      this.map.setCenter(point)
      this.openInfoCard({
        title: parking.vehicleNo || this.detail.vehicleNo || this.detail.vno || '-',
        status: '停车事件',
        statusColor: '#e6a23c',
        rows: [
          { label: '停车位置：', value: parking.locationName || '-', color: '#1677ff', navigation: true },
          { label: '开始时间：', value: this.formatDate(parking.startTime) },
          { label: parking.ended === false ? '末次观测：' : '结束时间：', value: this.formatDate(parking.endTime) },
          { label: '停车时长：', value: this.formatParkingDuration(parking.duration) },
          { label: '停车坐标：', value: formatLonLat(point) }
        ],
        point,
        navigationAddress: parking.locationName || '停车点'
      })
    },
    alarmWgs84Point(alarm) {
      const values = String((alarm && alarm.alarmLonlat) || '').split(/[,，\s]+/).filter(Boolean)
      if (values.length >= 2) {
        let longitude = Number(values[0])
        let latitude = Number(values[1])
        if (Math.abs(longitude) > 180 || Math.abs(latitude) > 90) {
          longitude /= 600000
          latitude /= 600000
        }
        if (Number.isFinite(longitude) && Number.isFinite(latitude) && Math.abs(longitude) <= 180 && Math.abs(latitude) <= 90) return [longitude, latitude]
      }
      return null
    },
    alarmPoint(alarm) {
      const point = this.alarmWgs84Point(alarm)
      if (point) return wgs84LonLatToGcj02Point(`${point[0]},${point[1]}`)
      const alarmTime = this.trackTimestamp({ locationTime: alarm && alarm.alarmTime })
      const nearest = this.tracks.reduce((selected, track) => {
        if (!this.hasTrackCoordinate(track)) return selected
        if (!selected || Math.abs(this.trackTimestamp(track) - alarmTime) < Math.abs(this.trackTimestamp(selected) - alarmTime)) return track
        return selected
      }, null)
      return nearest ? wgs84LonLatToGcj02Point(`${nearest.longitude},${nearest.latitude}`) : null
    },
    focusFence(fence) {
      const points = wgs84RouteToGcj02(fence && fence.fenceCoords)
      if (!points.length || !this.map) {
        this.$message.warning('该围栏暂无可定位坐标')
        return
      }
      if (Number(fence.fenceType) === 1) {
        this.map.setZoom(16)
        this.map.setCenter(points[0])
      } else if (points.length > 1) {
        const bounds = new AMap.Bounds(points[0], points[0])
        points.slice(1).forEach(point => bounds.extend(point))
        this.map.setBounds(bounds)
      }
      this.showFenceInfo(fence, points[0])
    },
    parkingAddressKey(point) {
      const longitude = Number(point && point.longitude)
      const latitude = Number(point && point.latitude)
      return Number.isFinite(longitude) && Number.isFinite(latitude) ? `${longitude.toFixed(6)},${latitude.toFixed(6)}` : ''
    },
    alarmAddressKey(alarm) {
      const point = this.alarmWgs84Point(alarm)
      return point ? `${point[0].toFixed(6)},${point[1].toFixed(6)}` : String((alarm && (alarm.id || alarm.alarmNo)) || `${alarm && alarm.alarmType}-${alarm && alarm.alarmTime}`)
    },
    alarmAddress(alarm) {
      return (alarm && alarm.alarmAddress) || this.alarmAddressMap[this.alarmAddressKey(alarm)] || ''
    },
    async reverseGeocodeMapPoint(point) {
      if (!point) return ''
      const result = await tiandituReverseGeocode(point[0], point[1])
      if (result && result.success && isValidResolvedAddress(result.des)) return result.des
      return resolveAddressByCoordUtil(point[0], point[1], {
        AMap: typeof window !== 'undefined' ? window.AMap : null
      })
    },
    async resolveParkingAddresses() {
      const pending = this.parkingPoints.filter((parking) => {
        const key = this.parkingAddressKey(parking)
        return key && !parking.locationName && this.parkingAddressMap[key] === undefined
      })
      if (!pending.length) return
      await Promise.all(pending.map(async (parking) => {
        const key = this.parkingAddressKey(parking)
        const point = wgs84LonLatToGcj02Point(`${parking.longitude},${parking.latitude}`)
        this.$set(this.parkingAddressMap, key, '解析中…')
        const resolvedAddress = await this.reverseGeocodeMapPoint(point)
        const address = resolvedAddress || '暂无定位地址'
        this.$set(this.parkingAddressMap, key, address)
        this.syncParkingAddressToTrajectory(parking, address)
      }))
    },
    syncParkingAddressToTrajectory(parking, address) {
      const trajectory = this.getTrajectory()
      if (!trajectory || !Array.isArray(trajectory.showTrackParkingDetailList)) return
      const current = trajectory.showTrackParkingDetailList.find(item => Number(item.beginGpsTime) === Number(parking.startTime))
      if (current) current.parkAdr = address
    },
    async resolveAlarmAddresses() {
      const alarms = this.alarms.filter(alarm => !alarm.alarmAddress && this.alarmPoint(alarm))
      await Promise.all(alarms.map(alarm => this.resolveAlarmAddress(alarm, this.alarmPoint(alarm))))
    },
    async resolveAlarmAddress(alarm, point) {
      const oldAddress = alarm && alarm.alarmAddress
      if (oldAddress) return oldAddress
      const key = this.alarmAddressKey(alarm)
      if (this.alarmAddressMap[key] && this.alarmAddressMap[key] !== '解析中…') return this.alarmAddressMap[key]
      if (!point) return ''
      this.$set(this.alarmAddressMap, key, '解析中…')
      const resolvedAddress = await this.reverseGeocodeMapPoint(point)
      const address = resolvedAddress || '暂无定位地址'
      this.$set(this.alarmAddressMap, key, address)
      return address
    },
    renderFences(overlays) {
      this.mapFences.forEach((fence) => {
        const points = wgs84RouteToGcj02(fence.fenceCoords)
        if (!points.length) return
        const options = {
          strokeColor: '#7c4dff',
          strokeWeight: 2,
          strokeOpacity: .9,
          fillColor: '#b39ddb',
          fillOpacity: .22,
          zIndex: 10
        }
        const overlay = Number(fence.fenceType) === 1
          ? new AMap.Circle({ ...options, center: points[0], radius: Number(fence.fenceRadius) || 500 })
          : new AMap.Polygon({ ...options, path: points })
        overlay.on('click', () => this.showFenceInfo(fence, points[0]))
        overlay.setMap(this.map)
        overlays.push(overlay)
      })
    },
    showNodeInfo(node, point) {
      if (!this.infoWindow) return
      const contact = node.contactName ? `${node.contactName}${node.contactPhone ? ` ${node.contactPhone}` : ''}` : '-'
      const statusColor = { origin: '#1dc588', stopover: '#e6a23c', target: '#ff0042' }[node.type] || '#2f78ed'
      const rows = [
        { label: '地址：', value: node.fullAddress || '-', color: '#1677ff', navigation: true },
        { label: '坐标：', value: this.nodeCoordinateText(node.lonLat) || '-' },
        { label: '联系人：', value: contact }
      ]
      if (node.planArriveTime) rows.push({ label: '预计到达：', value: this.formatDate(node.planArriveTime) })
      this.openInfoCard({ title: node.label, status: '运输节点', statusColor, rows, point, navigationAddress: node.fullAddress || node.label })
    },
    openInfoCard({ title, status, statusColor, rows, point, navigationAddress }) {
      this.vehicleInfoFollowing = false
      const root = document.createElement('div')
      root.style.cssText = 'display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 2px 8px rgba(0,0,0,.12))'
      const card = document.createElement('div')
      card.style.cssText = 'position:relative;width:300px;box-sizing:border-box;padding:12px 16px;background:#fff;border-radius:10px;font-size:13px;color:#303133;line-height:1.55'
      const closeButton = document.createElement('span')
      closeButton.textContent = '×'
      closeButton.style.cssText = 'position:absolute;right:10px;top:10px;z-index:1;cursor:pointer;font-size:20px;line-height:1;color:#c0c4cc;user-select:none'
      closeButton.addEventListener('click', (event) => {
        event.stopPropagation()
        if (this.infoWindow) this.infoWindow.close()
      })
      const titleRow = document.createElement('div')
      titleRow.style.cssText = 'display:flex;justify-content:space-between;align-items:flex-start;gap:8px;padding:2px 28px 0 0'
      const titleNode = document.createElement('strong')
      titleNode.textContent = title || '-'
      titleNode.style.cssText = 'font-size:16px;font-weight:700;color:#303133'
      const statusNode = document.createElement('span')
      statusNode.textContent = status || ''
      statusNode.style.cssText = `flex-shrink:0;font-size:14px;font-weight:600;color:${statusColor || '#2f78ed'};white-space:nowrap;padding-top:2px`
      titleRow.appendChild(titleNode)
      titleRow.appendChild(statusNode)
      card.appendChild(closeButton)
      card.appendChild(titleRow)
      const divider = document.createElement('div')
      divider.style.cssText = 'border-top:1px solid #ebeef5;margin:0'
      card.appendChild(divider)
      const content = document.createElement('div')
      content.style.cssText = 'padding-top:10px;font-size:12px;color:#606266'
      ;(rows || []).forEach((row) => {
        const line = document.createElement('div')
        const label = document.createElement('span')
        label.textContent = row.label
        const value = document.createElement('span')
        value.textContent = row.value
        value.style.cssText = `color:${row.color || '#303133'};word-break:break-all`
        line.appendChild(label)
        line.appendChild(value)
        if (row.navigation && point && Number.isFinite(Number(point[0])) && Number.isFinite(Number(point[1]))) {
          const navigation = document.createElement('a')
          navigation.href = `https://uri.amap.com/navigation?to=${Number(point[0]).toFixed(6)},${Number(point[1]).toFixed(6)},${encodeURIComponent(navigationAddress || row.value || '目的地')}`
          navigation.target = '_blank'
          navigation.rel = 'noopener noreferrer'
          navigation.textContent = '导航'
          navigation.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;margin-left:8px;padding:1px 6px;border-radius:4px;background:#1677ff;color:#fff;text-decoration:none;font-size:12px;line-height:1.5;white-space:nowrap'
          navigation.addEventListener('click', (event) => event.stopPropagation())
          line.appendChild(navigation)
        }
        content.appendChild(line)
      })
      card.appendChild(content)
      root.appendChild(card)
      const pointer = document.createElement('div')
      pointer.style.cssText = 'width:0;height:0;margin-top:-1px;border-left:9px solid transparent;border-right:9px solid transparent;border-top:10px solid #fff;flex-shrink:0'
      root.appendChild(pointer)
      this.infoWindow.close()
      this.infoWindow = new AMap.InfoWindow({ isCustom: true, content: root, anchor: 'bottom-center', offset: new AMap.Pixel(0, -19), autoMove: false })
      this.infoWindow.open(this.map, point)
    },
    showFenceInfo(fence, point) {
      this.openInfoCard({
        title: fence.fenceName || fence.fenceCode,
        status: '途中围栏',
        statusColor: '#7c4dff',
        rows: [
          { label: '围栏编码：', value: fence.fenceCode || '-' },
          { label: '围栏类型：', value: this.fenceTypeText(fence.fenceType) },
          { label: '监控事件：', value: this.eventTypeText(fence.eventType) }
        ],
        point,
        navigationAddress: fence.fenceName || fence.fenceCode
      })
    },
    showCurrentInfo(track, point) {
      this.vehicleInfoFollowing = true
      if (this.showTrajectoryVehicleInfo(track, point)) return
      if (!this.infoWindow) return
      const vehicleNo = this.detail.vehicleNo || track.vehicleNo || '-'
      const monitorStopped = [2, 3, 4].includes(Number(this.detail.transportStatus))
      const status = monitorStopped ? '已停止监控' : (this.monitor && Number(this.monitor.offlineStatus) === 1 ? '离线' : '在线')
      const statusColor = monitorStopped || status === '离线' ? '#909399' : '#52c41a'
      const speed = Number(track.speed)
      const speedText = Number.isFinite(speed) ? `${speed.toFixed(speed % 1 === 0 ? 0 : 1)}km/h` : '-'
      const direction = Number(track.direction)
      const directionText = Number.isFinite(direction) ? this.directionText(direction) : '-'
      const mileage = Number(track.mileage)
      const mileageText = Number.isFinite(mileage) ? `${mileage.toFixed(mileage % 1 === 0 ? 0 : 1)} km` : '-'
      const displayPoint = wgs84LonLatToGcj02Point(`${track.longitude},${track.latitude}`)
      const longitudeText = displayPoint ? displayPoint[0].toFixed(6) : '-'
      const latitudeText = displayPoint ? displayPoint[1].toFixed(6) : '-'
      const address = this.currentLocationText === '-' ? '暂无定位地址' : this.currentLocationText
      const root = document.createElement('div')
      root.style.cssText = 'display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 2px 8px rgba(0,0,0,.12))'
      const card = document.createElement('div')
      card.style.cssText = 'position:relative;width:300px;box-sizing:border-box;padding:12px 16px;background:#fff;border-radius:10px;font-size:13px;color:#303133;line-height:1.55'
      const closeButton = document.createElement('span')
      closeButton.textContent = '×'
      closeButton.style.cssText = 'position:absolute;right:10px;top:10px;z-index:1;cursor:pointer;font-size:20px;line-height:1;color:#c0c4cc;user-select:none'
      closeButton.addEventListener('click', (event) => {
        event.stopPropagation()
        if (this.infoWindow) this.infoWindow.close()
      })
      const header = document.createElement('div')
      header.style.cssText = 'padding:2px 28px 0 0'
      const titleRow = document.createElement('div')
      titleRow.style.cssText = 'display:flex;justify-content:space-between;align-items:flex-start;gap:8px'
      const title = document.createElement('strong')
      title.textContent = vehicleNo
      title.style.cssText = 'font-size:16px;font-weight:700;color:#303133'
      const statusText = document.createElement('span')
      statusText.textContent = status
      statusText.style.cssText = `flex-shrink:0;font-size:14px;font-weight:600;color:${statusColor};white-space:nowrap;padding-top:2px`
      titleRow.appendChild(title)
      titleRow.appendChild(statusText)
      header.appendChild(titleRow)
      card.appendChild(closeButton)
      card.appendChild(header)
      const divider = document.createElement('div')
      divider.style.cssText = 'border:0;border-top:1px solid #ebeef5;margin:0'
      card.appendChild(divider)
      const content = document.createElement('div')
      content.style.cssText = 'padding-top:10px;font-size:12px;color:#606266'
      const appendLine = (label, value, valueColor) => {
        const line = document.createElement('div')
        const labelNode = document.createElement('span')
        labelNode.textContent = label
        const valueNode = document.createElement('span')
        valueNode.textContent = value
        valueNode.style.color = valueColor || '#303133'
        line.appendChild(labelNode)
        line.appendChild(valueNode)
        content.appendChild(line)
      }
      appendLine('速度：', speedText)
      appendLine('方向：', directionText)
      appendLine('累计里程：', mileageText)
      if (this.vehicleMonitorInfo) {
        appendLine('剩余里程：', this.vehicleMonitorInfo.remainingDistance)
        appendLine('预计到达：', this.vehicleMonitorInfo.estimatedArriveTime)
      }
      appendLine('经度：', `${longitudeText}    纬度：${latitudeText}`)
      const locationLine = document.createElement('div')
      const locationLabel = document.createElement('span')
      locationLabel.textContent = '当前位置：'
      const locationValue = document.createElement('span')
      locationValue.textContent = address
      locationValue.style.cssText = 'color:#1677ff;word-break:break-all'
      locationLine.appendChild(locationLabel)
      locationLine.appendChild(locationValue)
      const mapLongitude = Number(point && point[0])
      const mapLatitude = Number(point && point[1])
      if (Number.isFinite(mapLongitude) && Number.isFinite(mapLatitude)) {
        const navigation = document.createElement('a')
        navigation.href = `https://uri.amap.com/navigation?to=${mapLongitude.toFixed(6)},${mapLatitude.toFixed(6)},${encodeURIComponent(address)}`
        navigation.target = '_blank'
        navigation.rel = 'noopener noreferrer'
        navigation.textContent = '导航'
        navigation.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;margin-left:8px;padding:1px 6px;border-radius:4px;background:#1677ff;color:#fff;text-decoration:none;font-size:12px;line-height:1.5;white-space:nowrap'
        navigation.addEventListener('click', (event) => event.stopPropagation())
        locationLine.appendChild(navigation)
      }
      content.appendChild(locationLine)
      appendLine('更新时间：', this.formatDate(track.locationTime))
      card.appendChild(content)
      root.appendChild(card)
      const pointer = document.createElement('div')
      pointer.style.cssText = 'width:0;height:0;margin-top:-1px;border-left:9px solid transparent;border-right:9px solid transparent;border-top:10px solid #fff;flex-shrink:0'
      root.appendChild(pointer)
      this.infoWindow.close()
      this.infoWindow = new AMap.InfoWindow({
        isCustom: true,
        content: root,
        anchor: 'bottom-center',
        offset: new AMap.Pixel(0, -19),
        autoMove: false
      })
      this.infoWindow.open(this.map, point)
    },
    showTrajectoryVehicleInfo(track, point) {
      const trajectory = this.getTrajectory()
      if (!trajectory || !trajectory.infoWindow || typeof trajectory.showInfoWindow !== 'function' || !point) return false
      const targetTime = this.trackTimestamp(track)
      const trace = (trajectory.traceList || []).reduce((closest, item) => {
        if (!closest || Math.abs(this.trackTimestamp({ gpsTime: item.gpsTime }) - targetTime) < Math.abs(this.trackTimestamp({ gpsTime: closest.gpsTime }) - targetTime)) return item
        return closest
      }, null)
      if (!trace) return false
      if (this.infoWindow) this.infoWindow.close()
      trajectory.showInfoWindow({
        lonlat: point,
        infoData: {
          ...trace,
          addr: (track && track.locationName) || this.currentLocationText || '-',
          vno: this.detail.vehicleNo || this.detail.vno || (track && track.vehicleNo) || '-'
        }
      })
      return true
    },
    normalizeNode(node, type, label, key) {
      const areaCodes = Array.isArray(node.areaCodes) ? node.areaCodes : String(node.areaCodes || '').split(',').filter(Boolean)
      const areaName = node.areaName || ''
      const address = node.address || ''
      return {
        key,
        type,
        label,
        areaCodes,
        areaName,
        address,
        fullAddress: areaName && !address.startsWith(areaName) ? `${areaName}${address}` : (address || areaName),
        lonLat: node.lonLat || node.lonlat || '',
        contactName: node.contactName || '',
        contactPhone: node.contactPhone || '',
        radius: node.radius,
        planArriveTime: node.planArriveTime || null
      }
    },
    hasTrackCoordinate(track) {
      return track && [track.longitude, track.latitude].every(value => value !== null && value !== undefined && String(value).trim() !== '' && Number.isFinite(Number(value)))
        && Math.abs(Number(track.longitude)) <= 180 && Math.abs(Number(track.latitude)) <= 90
    },
    trackIdentity(track) {
      if (!track) return ''
      if (track.id !== null && track.id !== undefined) return `id:${track.id}`
      const timestamp = this.trackTimestamp(track)
      return timestamp ? `time:${timestamp}` : ''
    },
    trackTimestamp(track) {
      const value = track && (track.locationTime || track.gpsTime || track.utc)
      if (value === null || value === undefined || value === '') return 0
      const numberValue = Number(value)
      if (Number.isFinite(numberValue)) return numberValue < 100000000000 ? numberValue * 1000 : numberValue
      const timestamp = dayjs(value).valueOf()
      return Number.isFinite(timestamp) ? timestamp : 0
    },
    toGtm(value) { return value && dayjs(value).isValid() ? dayjs(value).format('YYYYMMDD/HHmmss') : '' },
    formatDate(value) { return value && dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-' },
    formatAlarmLonLat(alarm) {
      const point = this.alarmWgs84Point(alarm)
      return point ? `${point[0].toFixed(6)},${point[1].toFixed(6)}` : '-'
    },
    formatParkingDuration(value) {
      const seconds = Math.max(0, Math.floor(Number(value) || 0))
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      return hours ? `${hours}小时${minutes}分钟` : `${minutes}分钟`
    },
    formatDistance(value) {
      if (value === null || value === undefined || value === '') return '-'
      const distance = Number(value)
      return Number.isFinite(distance) ? `${distance.toFixed(2).replace(/\.00$/, '')} km` : '-'
    },
    directionText(value) {
      const degree = Number(value)
      if (!Number.isFinite(degree)) return '-'
      const texts = ['正北', '东北', '正东', '东南', '正南', '西南', '正西', '西北']
      return `${texts[Math.round(degree / 45) % 8]} ${Math.round(degree)}°`
    },
    statusText(status) { return { 0: '待开始', 1: '运输中', 2: '已完成', 3: '已作废', 4: '运单监控结束' }[Number(status)] || '-' },
    alarmTypeText(type) { return ALARM_TYPE_TEXT[String(type)] || type || '-' },
    alarmTypeTag(type) {
      if (['1', '2'].includes(String(type))) return 'warning'
      if (['3', 'H', 'I', 'J'].includes(String(type))) return 'danger'
      if (['4', '5'].includes(String(type))) return 'info'
      return 'success'
    },
    fenceTypeText(type) { return Number(type) === 1 ? '圆形围栏' : '多边形围栏' },
    eventTypeText(type) { return { 1: '驶入', 2: '驶出', 3: '区域内停车', 4: '驶入+驶出', 5: '驶入+驶出+驶出超时', 6: '驶入+区域内停车', 7: '驶入+驶出+区域内停车', 8: '驶入+驶出+区域内停车+驶出超时' }[Number(type)] || '-' },
    clearExternalOverlays() {
      (this.externalOverlays || []).forEach((overlay) => {
        try { overlay.setMap(null) } catch (e) { /* 地图已销毁时无需处理。 */ }
      })
      this.externalOverlays = []
      if (this.infoWindow) this.infoWindow.close()
    },
    destroyMap() {
      this.clearExternalOverlays()
      this.map = null
      this.infoWindow = null
    }
  }
}
</script>

<style>
.waybill-map-dialog { max-width: calc(100vw - 32px); background: #fff; }
.waybill-map-dialog .el-dialog__header, .waybill-map-dialog .el-dialog__body, .waybill-map-dialog .el-dialog__footer { background: #fff; }
.waybill-map-dialog .el-dialog__body { padding: 0 20px; }
.waybill-map-dialog.dialog-window > .el-dialog__body { display: flex; flex-direction: column; overflow: hidden; }
.waybill-map-dialog.dialog-window .waybill-map-viewer { min-height: 0; height: auto; max-height: none; flex: 1; }
.waybill-map-dialog.is-fullscreen .el-dialog__body { height: calc(100vh - 124px); box-sizing: border-box; }
.waybill-map-dialog.is-fullscreen .waybill-map-viewer { height: 100%; max-height: none; }
</style>

<style scoped>
.waybill-map-route { display: flex; height: 100%; min-height: 0; max-height: 100%; flex-direction: column; overflow: hidden; background: #fff; }
.waybill-map-route .waybill-map-viewer { min-height: 0; height: 100%; flex: 1; border: 0; border-radius: 0; }
.waybill-map-viewer { position: relative; display: flex; height: min(68vh, 620px); min-height: 480px; overflow: hidden; border: 1px solid #e7ebf2; border-radius: 6px; background: #fff; }
.waybill-map-viewer__sidebar { width: 340px; min-height: 0; flex: 0 0 340px; overflow: hidden; border-right: 1px solid #e7ebf2; background: #fbfcfe; }
.waybill-map-viewer__tabs { display: flex; height: 100%; flex-direction: column; }
.waybill-map-viewer__tabs ::v-deep .el-tabs__header { flex: 0 0 auto; margin: 0; padding: 0 12px; background: #fff; }
.waybill-map-viewer__tabs ::v-deep .el-tabs__nav-wrap::after { background-color: #e7ebf2; }
.waybill-map-viewer__tabs ::v-deep .el-tabs__item { height: 42px; line-height: 42px; font-size: 13px; }
.waybill-map-viewer__tabs ::v-deep .el-tabs__content { min-height: 0; flex: 1; }
.waybill-map-viewer__tabs ::v-deep .el-tab-pane { height: 100%; overflow-y: auto; }
.waybill-map-viewer__map-wrap { position: relative; min-width: 0; flex: 1; }
.waybill-map-viewer__map { width: 100%; height: 100%; }
.waybill-map-viewer--joint .waybill-map-viewer__sidebar { order: 2; border-right: 0; border-left: 1px solid #e7ebf2; }
.waybill-map-viewer--events .waybill-map-viewer__sidebar { display: flex; width: 360px; flex: 0 0 360px; flex-direction: column; order: 2; border-right: 0; border-left: 1px solid #e7ebf2; background: #fff; }
.waybill-map-viewer--events-collapsed .waybill-map-viewer__sidebar { position: absolute; top: 14px; right: 14px; z-index: 160; width: 170px; max-width: calc(100% - 28px); height: auto; border: 0; border-radius: 4px; box-shadow: 0 2px 10px rgba(31,45,61,.08); }
.viewer-event-header { display: flex; min-height: 56px; flex: 0 0 56px; align-items: center; justify-content: space-between; box-sizing: border-box; padding: 0 15px 0 20px; border-bottom: 1px solid #e7ebf2; }
.viewer-event-header__title { display: flex; align-items: center; padding: 0; border: 0; background: transparent; color: #303133; font-family: inherit; font-size: 17px; font-weight: 600; text-align: left; cursor: pointer; }
.viewer-event-header__title span { display: inline-block; margin-left: 7px; padding: 2px 8px; border-radius: 12px; background: #fff1f0; color: #f5222d; font-size: 12px; font-weight: 400; }
.viewer-event-header__toggle { display: flex; width: 26px; height: 30px; align-items: center; justify-content: center; padding: 0; border: 0; background: transparent; color: #303133; font-size: 18px; cursor: pointer; }
.waybill-map-viewer--events-collapsed .viewer-event-header { min-height: 50px; flex-basis: 50px; padding: 0 10px; border: 0; background: #007aff; }
.waybill-map-viewer--events-collapsed .viewer-event-header__title { color: #fff; font-size: 14px; font-weight: 400; }
.waybill-map-viewer--events-collapsed .viewer-event-header__title span { min-width: 16px; height: 16px; box-sizing: border-box; margin-left: 6px; padding: 0 3px; border-radius: 8px; background: #fff; color: #ff2121; font-size: 12px; line-height: 16px; text-align: center; vertical-align: 1px; }
.waybill-map-viewer--events-collapsed .viewer-event-header__toggle { width: 20px; height: 24px; color: #fff; font-size: 16px; }
.viewer-event-compact { display: flex; min-height: 40px; align-items: center; justify-content: space-around; gap: 4px; padding: 0 6px; background: #fff; color: rgba(0,0,0,.25); font-size: 12px; }
.viewer-event-compact em { color: #ff2121; font-style: normal; }
.viewer-event-compact button { padding: 4px 0; border: 0; background: transparent; color: inherit; font: inherit; cursor: pointer; }
.viewer-event-compact button:hover, .viewer-event-compact button.is-active { color: #1677ff; }
.viewer-event-header__title:focus-visible, .viewer-event-compact button:focus-visible { outline: 2px solid #1677ff; outline-offset: 2px; }
.viewer-event-popup { position: absolute; top: 14px; right: 372px; z-index: 220; display: flex; width: 310px; max-width: calc(100% - 28px); max-height: calc(100% - 28px); flex-direction: column; border: 1px solid #e7ebf2; border-radius: 6px; background: #fff; box-shadow: 0 5px 18px rgba(31,45,61,.15); }
.viewer-event-popup--compact { right: 196px; }
.viewer-event-popup > header { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 16px; border-bottom: 1px solid #e7ebf2; color: #344054; }
.viewer-event-popup > header strong { display: block; font-size: 16px; }
.viewer-event-popup small { display: block; margin-top: 6px; color: #909399; font-size: 12px; line-height: 19px; }
.viewer-event-popup > header button { padding: 4px; border: 0; background: transparent; color: #909399; font-size: 18px; cursor: pointer; }
.viewer-event-popup__body { min-height: 0; overflow-y: auto; padding: 0 16px; }
.viewer-event-popup__item { display: block; width: 100%; padding: 16px 0; border: 0; border-bottom: 1px solid #e7ebf2; background: transparent; color: #606266; font-family: inherit; text-align: left; cursor: pointer; }
.viewer-event-popup__item:hover { background: #f5f8ff; }
.viewer-event-popup__item > div { display: flex; justify-content: space-between; align-items: center; gap: 8px; color: #344054; font-size: 15px; }
.viewer-event-popup__item p { margin: 8px 0 0; font-size: 13px; line-height: 20px; overflow-wrap: anywhere; }
.viewer-event-popup__error { padding: 10px 16px; color: #f56c6c; font-size: 12px; }
.viewer-event-popup .viewer-empty { padding: 24px 0; text-align: center; }
@media (max-width: 900px) { .viewer-event-popup { top: 108px; right: 14px; max-height: calc(100% - 122px); } }
.viewer-event-panel { display: flex; min-height: 0; height: 100%; flex: 1; flex-direction: column; }
.viewer-event-controls { position: relative; display: flex; min-height: 48px; flex: 0 0 48px; align-items: center; justify-content: center; gap: 18px; padding: 0 20px; border-bottom: 1px solid #f0f2f5; }
.viewer-event-controls button { padding: 0 4px; border: 0; background: transparent; color: #303133; font-size: 14px; line-height: 28px; cursor: pointer; }
.viewer-event-controls button.is-active { color: #1677ff; }
.viewer-event-controls > button + button { padding-left: 20px; border-left: 1px solid #dcdfe6; }
.viewer-event-controls > span { position: absolute; right: 17px; }
.viewer-event-filter { color: #606266; font-size: 13px; line-height: 28px; }
.viewer-event-filter .el-checkbox-group { display: flex; flex-direction: column; }
.viewer-event-filter .el-checkbox { margin-right: 0; }
.viewer-event-filter__parking { display: flex; align-items: center; gap: 5px; margin-top: 8px; white-space: nowrap; }
.viewer-event-filter__parking .el-input-number { width: 82px; }
.waybill-map-viewer--events .waybill-map-viewer__tabs ::v-deep .el-tabs__header { display: none; }
.viewer-event-overview { display: flex; min-height: 0; flex: 1; flex-direction: column; }
.viewer-event-overview > .waybill-map-viewer__tabs { min-height: 0; height: auto; flex: 1; }
.viewer-track-summary { padding: 15px 20px 10px; border-bottom: 1px solid #e7ebf2; }
.viewer-track-summary dl { display: grid; grid-template-columns: 90px 1fr; gap: 7px 10px; margin: 0 0 13px; font-size: 13px; line-height: 20px; }
.viewer-track-summary dt { color: #909399; }
.viewer-track-summary dd { margin: 0; color: #303133; }
.viewer-track-summary__parking { display: flex; justify-content: space-between; gap: 10px; color: #606266; font-size: 12px; }
.viewer-track-summary p { margin: 10px 0 0; color: #909399; font-size: 11px; line-height: 17px; }
.viewer-event-detail { min-height: 0; flex: 1; overflow-y: auto; }
.viewer-event-range { padding: 13px 20px 0; color: #909399; font-size: 11px; line-height: 19px; }
.viewer-event-pagination { display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 4px 10px; padding: 0 20px 15px; color: #909399; font-size: 12px; }
.waybill-map-viewer--events .waybill-map-viewer__toolbar { left: 14px; right: auto; }
.waybill-map-viewer--events .waybill-map-viewer__toolbar ::v-deep .top-controls { align-items: flex-start; }
.waybill-map-viewer--events .waybill-map-viewer__layers { top: 142px; }
.viewer-event-item { display: grid; gap: 5px; margin-bottom: 10px; padding: 10px; border-radius: 4px; background: #f5f7fa; color: #475467; font-size: 12px; }
.viewer-event-item > span { color: #98a2b3; }
.viewer-event-item--locatable { cursor: pointer; }
.viewer-parking-filter { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; font-size: 12px; }
.viewer-parking-filter .el-input-number { width: 110px; }
.viewer-parking-summary { margin-bottom: 12px; color: #909399; font-size: 12px; }
.waybill-map-viewer__playback { position: absolute; inset: 0; z-index: 140; pointer-events: none; }
.waybill-map-viewer__playback ::v-deep * { pointer-events: auto; }
.waybill-map-viewer__playback ::v-deep .RightData { position: relative; width: 100%; height: 100%; }
.waybill-map-viewer__playback ::v-deep .trip-info-popup { left: 0 !important; width: 100% !important; }
.waybill-map-viewer__playback ::v-deep .trip-info { display: none !important; }
.waybill-map-viewer__playback ::v-deep .expand-controls-top,
.waybill-map-viewer__playback ::v-deep .expand-controls-bottom {
  position: absolute !important;
  left: 50% !important;
  z-index: 160 !important;
}
.waybill-map-viewer__playback ::v-deep .expand-controls-top { top: 0 !important; }
.waybill-map-viewer__playback ::v-deep .expand-controls-bottom { bottom: 0 !important; }
.viewer-tab-label { display: inline-flex; align-items: center; gap: 5px; }
.viewer-tab-label__back { color: #2f78ed; font-size: 16px; cursor: pointer; }
.viewer-section { padding: 16px; border-bottom: 1px solid #e7ebf2; }
.viewer-section__title { margin-bottom: 12px; color: #1f2d3d; font-size: 14px; font-weight: 600; }
.viewer-section__title::before { display: inline-block; width: 3px; height: 14px; margin-right: 7px; vertical-align: -2px; border-radius: 2px; background: #2f78ed; content: ''; }
.viewer-section__title .el-button { float: right; padding: 0; font-weight: 400; }
.viewer-tab-count { display: inline-flex; align-items: center; justify-content: center; min-width: 16px; height: 16px; margin-left: 3px; padding: 0 4px; border-radius: 9px; background: #fff1f0; color: #f5222d; font-size: 10px; font-style: normal; line-height: 16px; vertical-align: 1px; }
.viewer-basic-grid { display: grid; grid-template-columns: 76px minmax(0, 1fr); gap: 8px 10px; color: #667085; font-size: 12px; }
.viewer-basic-grid strong { min-width: 0; color: #344054; font-weight: 500; line-height: 20px; overflow-wrap: anywhere; word-break: break-word; }
.viewer-basic-grid .viewer-waybill-no { display: flex; align-items: center; gap: 6px; overflow: visible; }
.viewer-waybill-no .el-button { padding: 0; font-size: 12px; font-weight: 400; }
.viewer-source { margin-left: 3px; color: #909399; font-size: 11px; font-style: normal; }
.viewer-monitor-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px 10px; }
.viewer-monitor-notice { margin: 12px 14px 0; padding: 12px; border: 1px solid #faecd8; border-radius: 4px; background: #fdf6ec; color: #7c5b25; }
.viewer-monitor-notice > strong { font-size: 14px; }
.viewer-monitor-notice > p { margin: 6px 0 0; font-size: 12px; line-height: 1.7; }
.viewer-monitor-item { display: flex; min-width: 0; align-items: center; color: #667085; font-size: 12px; line-height: 20px; }
.viewer-monitor-item > span { flex: 0 0 48px; color: #98a2b3; }
.viewer-monitor-item strong { min-width: 0; flex: 1; color: #344054; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.viewer-monitor-item--wide { grid-column: 1 / -1; }
.viewer-monitor-item--wide strong { overflow: visible; text-overflow: clip; white-space: normal; overflow-wrap: anywhere; word-break: break-word; }
.viewer-node-list, .viewer-fence-list { display: flex; flex-direction: column; gap: 9px; }
.viewer-node-item { display: flex; gap: 9px; min-width: 0; cursor: pointer; }
.viewer-node-item:hover { border-radius: 4px; background: #f0f6ff; }
.viewer-node-item__content { min-width: 0; flex: 1; }
.viewer-node-item__badge { display: inline-flex; align-items: center; justify-content: center; color: #fff; font-size: 12px; font-weight: 600; }
.viewer-node-item__badge--origin, .viewer-node-item__badge--target { width: 54px; height: 28px; flex: 0 0 54px; border-radius: 14px; white-space: nowrap; }
.viewer-node-item__badge--origin { background: #1dc588; }
.viewer-node-item__badge--target { background: #ff0042; }
.viewer-node-item__badge--stopover { width: 54px; height: 28px; flex: 0 0 54px; border-radius: 14px; background: #e6a23c; white-space: nowrap; }
.viewer-node-item__address { color: #344054; font-size: 13px; line-height: 20px; overflow-wrap: anywhere; word-break: break-word; }
.viewer-node-item__meta { margin-top: 3px; color: #98a2b3; font-size: 11px; line-height: 17px; overflow-wrap: anywhere; }
.viewer-fence-item { display: flex; flex-direction: column; gap: 3px; padding: 8px 10px; border-radius: 4px; background: #f3efff; cursor: pointer; }
.viewer-fence-item:hover { background: #ebe4ff; }
.viewer-fence-item strong { color: #4c3385; font-size: 13px; line-height: 20px; overflow-wrap: anywhere; word-break: break-word; }
.viewer-fence-item span, .viewer-empty { color: #7b6aa5; font-size: 12px; }
.viewer-empty { padding: 5px 0; color: #98a2b3; }
.viewer-alarm-list { display: flex; flex-direction: column; gap: 8px; }
.viewer-alarm-item { display: flex; align-items: flex-start; gap: 8px; padding: 8px 9px; border-radius: 4px; background: #fff8f6; }
.viewer-alarm-item--clickable { cursor: pointer; }
.viewer-alarm-item--clickable:hover { background: #fff1ed; }
.viewer-parking-item { display: flex; flex-direction: column; gap: 6px; background: #fffbf0; }
.viewer-parking-item__header { display: flex; align-items: center; gap: 7px; min-width: 0; }
.viewer-parking-item__header .el-tag { height: 23px; line-height: 21px; }
.viewer-parking-item__header strong { min-width: 0; color: #d98000; font-size: 14px; line-height: 23px; }
.viewer-parking-item:hover { background: #fff5df; }
.viewer-parking-item__address { color: #344054; font-size: 13px; line-height: 20px; overflow-wrap: anywhere; word-break: break-word; }
.viewer-alarm-item__content { min-width: 0; flex: 1; color: #475467; font-size: 12px; line-height: 18px; }
.viewer-alarm-item__content > div { overflow-wrap: anywhere; word-break: break-word; }
.viewer-alarm-item__content > span { display: block; margin-top: 2px; color: #98a2b3; font-size: 11px; }
.viewer-alarm-item__address { margin: 3px 0 0; color: #1677ff; font-size: 11px; line-height: 17px; overflow-wrap: anywhere; word-break: break-word; }
.viewer-parking-item__time { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; color: #667085; font-size: 11px; line-height: 18px; }
.viewer-parking-item__time span { min-width: 0; overflow-wrap: anywhere; word-break: break-word; }
.viewer-parking-item__time em { display: block; margin-bottom: 1px; color: #98a2b3; font-size: 11px; font-style: normal; }
.waybill-map-viewer__toolbar { position: absolute; top: 14px; right: 14px; z-index: 130; }
.waybill-map-viewer__track-detail-controls, .waybill-map-viewer__info-collapse-toggle { display: none; }
.waybill-map-viewer__layers { position: absolute; top: 14px; left: 14px; z-index: 130; display: flex; min-width: 112px; flex-direction: column; gap: 6px; padding: 9px 11px; border-radius: 5px; background: rgba(255,255,255,.96); box-shadow: 0 2px 8px rgba(31,45,61,.16); }
.waybill-map-viewer__layers-title { margin-bottom: 1px; color: #344054; font-size: 12px; font-weight: 600; }
.waybill-map-viewer__layers ::v-deep .el-checkbox { height: 18px; margin: 0; color: #475467; font-size: 12px; line-height: 18px; }
.waybill-map-viewer__layers ::v-deep .el-checkbox__label { padding-left: 5px; }
.layer-line { display: inline-block; width: 15px; height: 3px; margin-right: 4px; border-radius: 2px; vertical-align: 2px; }
.layer-line--plan { background: #f5222d; }
.layer-line--actual { background: #2fae55; }
.layer-line--estimate { background: repeating-linear-gradient(90deg, #7c4dff 0, #7c4dff 5px, transparent 5px, transparent 8px); }
@media (max-width: 900px) {
  .waybill-map-viewer__sidebar { width: 300px; flex-basis: 300px; }
  .waybill-map-viewer--events .waybill-map-viewer__sidebar { width: 320px; flex-basis: 320px; }
  .waybill-map-viewer--events-collapsed .waybill-map-viewer__sidebar { width: 170px; }
}
@media (max-width: 768px) {
  .waybill-map-viewer--share { flex-direction: column; }
  .waybill-map-viewer--share .waybill-map-viewer__map-wrap { order: 1; height: 62%; min-height: 0; flex: 0 0 62%; }
  .waybill-map-viewer--share .waybill-map-viewer__sidebar { order: 2; width: 100%; min-height: 0; flex: 1 1 0; border-top: 1px solid #e7ebf2; border-right: 0; }
  .waybill-map-viewer--share-info-collapsed .waybill-map-viewer__map-wrap { height: 100%; flex-basis: 100%; }
  .waybill-map-viewer--share-info-collapsed .waybill-map-viewer__sidebar { display: none; }
  .waybill-map-viewer--share .waybill-map-viewer__tabs ::v-deep .el-tabs__header { padding: 0; }
  .waybill-map-viewer--share .waybill-map-viewer__tabs ::v-deep .el-tabs__item { height: 40px; padding: 0 6px; font-size: 12px; line-height: 40px; }
  .waybill-map-viewer--share .viewer-section { padding: 12px; }
  .waybill-map-viewer--share .viewer-section__title { margin-bottom: 9px; }
  .waybill-map-viewer--share .viewer-basic-grid { grid-template-columns: 68px minmax(0, 1fr); gap: 6px 8px; }
  .waybill-map-viewer--share .viewer-monitor-grid { gap: 5px 8px; }
  .waybill-map-viewer--share .viewer-node-list, .waybill-map-viewer--share .viewer-fence-list { gap: 7px; }
  .waybill-map-viewer--share .waybill-map-viewer__layers { top: 8px; left: 8px; gap: 4px; min-width: 102px; padding: 6px 8px; }
  .waybill-map-viewer--share .waybill-map-viewer__layers ::v-deep .el-checkbox { font-size: 11px; }
  .waybill-map-viewer--share .waybill-map-viewer__toolbar { top: 8px; right: 8px; }
  .waybill-map-viewer--share .waybill-map-viewer__track-detail-controls { position: absolute; right: 8px; bottom: 160px; z-index: 170; display: flex; gap: 6px; }
  .waybill-map-viewer--share .waybill-map-viewer__track-detail-controls button, .waybill-map-viewer--share .waybill-map-viewer__info-collapse-toggle { display: inline-flex; align-items: center; gap: 5px; min-height: 34px; padding: 0 10px; border: 0; border-radius: 5px; background: rgba(255,255,255,.96); box-shadow: 0 2px 8px rgba(31,45,61,.16); color: #2f78ed; font-size: 13px; font-weight: 600; }
  .waybill-map-viewer--share .waybill-map-viewer__info-collapse-toggle { position: absolute; bottom: 160px; left: 8px; z-index: 170; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .expand-controls, .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .expand-controls-fiex { display: none !important; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .trip-info-popup { left: 0 !important; width: 100% !important; padding: 6px 8px; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .function-buttons-row { display: none !important; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .video-player { padding: 7px 8px; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .player-controls { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 7px 10px; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .player-controls__left { grid-column: 1; grid-row: 1; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .player-controls__right { grid-column: 2; grid-row: 1; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .player-controls__track { grid-column: 1 / -1; grid-row: 2; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .time-display { min-width: 48px; font-size: 13px; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .speed-picker { width:64px; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .speed-picker__trigger { height: 34px; padding: 0 4px; font-size: 13px; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .el-slider__runway { margin: 12px 0; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .el-slider__button { width: 20px; height: 20px; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .playback-range-bar { min-height: calc(16px + 2.25rem); padding-bottom: 3px; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .playback-range-bar__inner { min-height: calc(16px + 2.25rem); padding-top: 16px; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .playback-range-bar__chart { height: 2.25rem; }
  .waybill-map-viewer--share .waybill-map-viewer__playback ::v-deep .playback-range-bar__time { font-size: 11px; }
}
@media (max-width: 768px) and (orientation: landscape) {
  .waybill-map-viewer--share .waybill-map-viewer__map-wrap { height: 68%; flex-basis: 68%; }
}
</style>
